// Composant Planning Management - Version Mobile Optimisée
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from './api/http';
import { toast } from 'sonner';
import { 
  Calendar, 
  UserPlus, 
  Users, 
  Edit, 
  Trash2, 
  X, 
  Clock,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Mail,
  Pause,
  Play,
  CheckCircle,
  Loader2
} from 'lucide-react';
import TeamManagementDragDrop from './TeamManagementDragDrop';
import MissionComments from './components/MissionComments';
import { planningGroupKey, expandPlanningOverrides, fetchPlanningSchedules } from './lib/planningSchedules';
import { useConfirm } from './components/ui/confirm-dialog';

// Resolve API base URL consistently with App.js — utilise le hostname du navigateur pour supporter le LAN
const BACKEND_URL = (() => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (envUrl.startsWith('https://')) return envUrl;
  const host = (typeof window !== 'undefined') ? window.location.hostname : 'localhost';
  return `http://${host}:8001`;
})();
const API = process.env.REACT_APP_API_BASE_URL || `${BACKEND_URL}/api`;

// ⚡ Performance tracker
const _perfTimers = {};
const perf = {
  start: (label) => { _perfTimers[label] = performance.now(); },
  end: (label, threshold = 1000) => {
    if (!_perfTimers[label]) return;
    const ms = Math.round(performance.now() - _perfTimers[label]);
    const icon = ms > threshold ? '⚠️' : ms > 500 ? '⏱' : '⚡';
    console.log(`${icon} [PERF] ${label}: ${ms}ms`);
    delete _perfTimers[label];
    return ms;
  }
};

// Simple UI Components
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-[18px] border border-[rgba(15,23,42,0.08)] ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-4 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`px-4 pt-4 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold ${className}`}>
    {children}
  </h3>
);

const Button = ({ children, onClick, variant = "default", size = "default", className = "", disabled = false, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
  
  const variants = {
    default: "bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white hover:from-[#1d4ed8] hover:to-[#6d28d9] focus:ring-blue-500",
    outline: "border border-[rgba(15,23,42,0.12)] bg-white text-[#475569] hover:bg-slate-50 focus:ring-blue-500",
    ghost: "text-gray-700 hover:bg-gray-100 focus:ring-blue-500"
  };
  
  const sizes = {
    sm: "text-sm px-3 py-1.5",
    default: "text-sm px-4 py-2",
    lg: "text-base px-6 py-3"
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({ className = "", ...props }) => (
  <input
    className={`flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Badge = ({ children, className = "" }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}>
    {children}
  </span>
);

// Planning Management Component - Complete Team and Schedule Management
const PlanningManagement = () => {
  const confirm = useConfirm();
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const schedulesRequestId = useRef(0);
  const [invitations, setInvitations] = useState([]);
  const [acceptedInvites, setAcceptedInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planningError, setPlanningError] = useState(false);
  const [activeTab, setActiveTab] = useState('planning');
  const [showTeamLeaderForm, setShowTeamLeaderForm] = useState(false);
  const [showCollaboratorForm, setShowCollaboratorForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  // 🌙 Règle de majoration nuit + durée standard d'une journée (configurées dans Paramètres entreprise)
  const [nightSurcharge, setNightSurcharge] = useState(null); // { enabled, type, value, label } ou null si non chargé
  const [standardDayHours, setStandardDayHours] = useState(8); // valeur par défaut tant que le réglage n'est pas chargé
  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    axios.get(`${API}/company-settings`)
      .then(res => {
        const d = res.data || {};
        setNightSurcharge({
          enabled: !!d.night_surcharge_enabled,
          type: d.night_surcharge_type || 'percent',
          value: d.night_surcharge_value ?? 0,
          label: d.night_surcharge_label || 'Majoration nuit',
        });
        setStandardDayHours(parseFloat(d.standard_day_hours) || 7);
      })
      .catch(() => {
        setNightSurcharge({ enabled: false, type: 'percent', value: 0, label: 'Majoration nuit' });
        setStandardDayHours(7);
      });
  }, []);
  const formatNightSurcharge = (rule) => {
    if (!rule) return '';
    return rule.type === 'fixed' ? `+${parseFloat(rule.value).toFixed(2)}€` : `+${rule.value}%`;
  };
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  
  // États pour l'édition
  const [editingCollaborator, setEditingCollaborator] = useState(null);
  const [showEditCollaboratorModal, setShowEditCollaboratorModal] = useState(false);
  const [editCollaboratorData, setEditCollaboratorData] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  
  // Vue temporelle - nouveau
  const [viewMode, setViewMode] = useState('timeline'); // 'day', 'week', 'month', 'timeline'
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // NEW: Enhanced day detail modal state
  const [showDayDetail, setShowDayDetail] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [detailedDate, setDetailedDate] = useState(null);

  // Modal détails du créneau
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showScheduleDetail, setShowScheduleDetail] = useState(false);
  const [actionScope, setActionScope] = useState('single');
  const actionBase = selectedSchedule && (schedules.find(schedule => schedule.id === selectedSchedule.id) || selectedSchedule);
  const actionMembers = actionBase ? schedules.filter(schedule => planningGroupKey(schedule) === planningGroupKey(actionBase)) : [];
  const renderActionScope = (base = selectedSchedule, members = actionMembers, scope = actionScope, setScope = setActionScope, disabled = pauseLoading || completeLoading) => (
    <fieldset className="mb-4 space-y-2" disabled={disabled}>
      <legend className="text-sm font-semibold text-gray-700 mb-2">Affectations concernées</legend>
      <label className="flex items-center gap-2 text-sm">
        <input type="radio" name="action-scope" value="single" checked={scope === 'single'} onChange={() => setScope('single')} />
        {getCollaboratorName(base?.collaborator_id)} uniquement
      </label>
      {members.length > 1 && (
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="action-scope" value="group" checked={scope === 'group'} onChange={() => setScope('group')} />
          Toute l’intervention ({members.length} affectations)
        </label>
      )}
    </fieldset>
  );

  // Photos de la mission (uploadées par le technicien)
  const [missionPhotos, setMissionPhotos] = useState([]);
  const [missionPhotosLoading, setMissionPhotosLoading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [uploadingMissionPhoto, setUploadingMissionPhoto] = useState(false);
  
  // Modal pause / report
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseLoading, setPauseLoading] = useState(false);
  const [pauseMode, setPauseMode] = useState('pause'); // 'pause' | 'postpone'
  const [postponeOriginalDate, setPostponeOriginalDate] = useState('');
  const [postponeNewDate, setPostponeNewDate] = useState('');
  const [pauseDuration, setPauseDuration] = useState(''); // HH:MM — durée à reporter
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleOverride, setRescheduleOverride] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  // Interventions (overrides)
  const [overrides, setOverrides] = useState([]);
  const [interventionSubTab, setInterventionSubTab] = useState('completed'); // 'completed' | 'paused' | 'postponed' | 'cancelled'

  // Modal reprise avec reprogrammation
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeSchedule, setResumeSchedule] = useState(null);
  const [resumeData, setResumeData] = useState({ start_date: '', time: '', team_leader_id: '', collaborator_id: '' });
  const resumeMembers = resumeSchedule ? schedules.filter(schedule => planningGroupKey(schedule) === planningGroupKey(resumeSchedule)).sort((first, second) => first.id.localeCompare(second.id)) : [];
  const [resumeLoading, setResumeLoading] = useState(false);

  // Modal clôture de mission
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completionReport, setCompletionReport] = useState('');
  const [completeLoading, setCompleteLoading] = useState(false);

  // Modal d'édition du créneau
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  const [editScheduleData, setEditScheduleData] = useState({
    start_date: '',
    end_date: '',
    team_leader_id: '',
    collaborator_id: ''
  });
  const [saving, setSaving] = useState(false);

  // Modal d'assignation rapide
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // schedule ou {worksite_id, worksite_title, count}
  const [assignMode, setAssignMode] = useState('single'); // 'single' | 'batch'
  const [assignCollaboratorId, setAssignCollaboratorId] = useState('');
  const [assignTeamLeaderId, setAssignTeamLeaderId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  // Drag & drop
  const [draggedSchedule, setDraggedSchedule] = useState(null);

  // Modal de planification de chantier
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [selectedWorksiteForPlanning, setSelectedWorksiteForPlanning] = useState(null);
  const [planningData, setPlanningData] = useState({
    team_leader_id: '',
    collaborator_ids: [],
    start_date: '',
    end_date: '',
    notes: '',
    shift_type: 'day' // Nouveau champ pour le type d'intervention
  });
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  
  // Stocker les collaborateurs par chef d'équipe
  const [teamCollaborators, setTeamCollaborators] = useState({}); // { teamLeaderId: [collaborators] }

  // État pour sélectionner un technicien existant comme chef d'équipe
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);
  
  // Form states
  const [teamLeaderData, setTeamLeaderData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    specialite: '',
    couleur: '#3B82F6'
  });
  
  const [collaboratorData, setCollaboratorData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    competences: '',
    team_leader_id: ''
  });
  
  const [scheduleData, setScheduleData] = useState({
    intervention_category: 'worksite', // 'rdv', 'worksite', ou 'urgence'
    worksite_mode: 'existing', // 'existing' ou 'new'
    worksite_id: '',
    new_worksite_title: '',
    new_worksite_address: '',
    client_name: '',
    client_address: '',
    client_contact: '',
    team_leader_id: '',
    collaborator_id: '',
    additional_collaborators: [], // Pour ajouter plusieurs collaborateurs
    start_date: selectedDate,
    end_date: selectedDate,
    time: '08:00', // Heure de début
    shift: 'day', // day, night, morning, afternoon
    hours: '8',
    description: ''
  });

  const SHIFT_OPTIONS = [
    { value: 'day', label: `Journée complète (${standardDayHours}h)`, hours: standardDayHours },
    { value: 'morning', label: `Matinée (${standardDayHours / 2}h)`, hours: standardDayHours / 2 },
    { value: 'afternoon', label: `Après-midi (${standardDayHours / 2}h)`, hours: standardDayHours / 2 },
    { value: 'night', label: `Nuit (${standardDayHours}h)`, hours: standardDayHours }
  ];

  const SPECIALTIES = [
    'Détection de réseaux',
    'Géolocalisation',
    'Travaux publics',
    'Électricité',
    'Plomberie',
    'Maçonnerie',
    'Supervision'
  ];

  const COLORS = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#F97316', '#EC4899', '#06B6D4'
  ];

  // Helper : statut effectif (auto-start si date/heure dépassée)
  const getEffectiveStatus = (schedule) => {
    if (!schedule) return 'scheduled';
    if (schedule.status !== 'scheduled') return schedule.status;
    const startDate = schedule.start_date;
    const timeStr = schedule.time || '08:00:00';
    if (!startDate) return 'scheduled';
    try {
      const parts = timeStr.split(':');
      const dt = new Date(startDate + 'T' + parts[0].padStart(2,'0') + ':' + (parts[1] || '00').padStart(2,'0') + ':00');
      if (new Date() >= dt) return 'in_progress';
    } catch(e) { /* ignore */ }
    return 'scheduled';
  };

  useEffect(() => {
    loadPlanningData();
    
    // Vérifier si on doit ouvrir le modal de planification depuis les params URL
    const urlParams = new URLSearchParams(window.location.search);
    const worksiteId = urlParams.get('worksite');
    const action = urlParams.get('action');
    
    if (action === 'plan' && worksiteId) {
      // Charger le chantier et ouvrir le modal
      loadWorksiteAndOpenModal(worksiteId);
    }
  }, []);

  // ⚡ Recharger uniquement les schedules quand la vue/date change (pas au premier mount)
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    // Recharger les schedules avec le nouveau range de dates
    const reloadSchedules = async () => {
      const requestId = ++schedulesRequestId.current;
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { 'Authorization': `Bearer ${token}` };
      const d = currentDate;
      let fromDate, toDate;
      if (viewMode === 'month') {
        fromDate = new Date(d.getFullYear(), d.getMonth(), 1);
        toDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        fromDate.setDate(fromDate.getDate() - (fromDate.getDay() + 6) % 7);
        toDate.setDate(toDate.getDate() + (7 - toDate.getDay()) % 7);
      } else {
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        fromDate = new Date(d); fromDate.setDate(diff - 1);
        toDate = new Date(fromDate); toDate.setDate(fromDate.getDate() + 9);
      }
      const pad = (n) => String(n).padStart(2, '0');
      const fromStr = `${fromDate.getFullYear()}-${pad(fromDate.getMonth()+1)}-${pad(fromDate.getDate())}`;
      const toStr = `${toDate.getFullYear()}-${pad(toDate.getMonth()+1)}-${pad(toDate.getDate())}`;
      try {
        const [schedRes, ovRes] = await Promise.all([
          fetchPlanningSchedules(axios, `${API}/schedules?from=${fromStr}&to=${toStr}`, { headers }),
          axios.get(`${API}/planning-overrides`, { headers, timeout: 20000 }),
        ]);
        // Setter les deux ensemble pour éviter le flash
        if (requestId !== schedulesRequestId.current) return;
        setSchedules(schedRes.data || []);
        setOverrides(ovRes.data || []);
        setPlanningError(false);
      } catch (e) {
        if (requestId === schedulesRequestId.current) {
          setPlanningError(true);
          toast.error('Impossible de charger cette période du planning.');
        }
      } finally {
        if (requestId === schedulesRequestId.current) setLoading(false);
      }
    };
    reloadSchedules();
    return () => { schedulesRequestId.current += 1; };
  }, [viewMode, currentDate]);

  // 📸 Charger les photos de la mission quand le modal de détail s'ouvre
  useEffect(() => {
    if (!showScheduleDetail || !selectedSchedule?.id) {
      setMissionPhotos([]);
      return;
    }
    let cancelled = false;
    const loadPhotos = async () => {
      setMissionPhotosLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        const res = await axios.get(`${API}/schedules/${selectedSchedule.id}/photos`, { headers, timeout: 8000 });
        if (!cancelled) setMissionPhotos(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setMissionPhotos([]);
        console.error('Erreur chargement photos mission:', e);
      } finally {
        if (!cancelled) setMissionPhotosLoading(false);
      }
    };
    loadPhotos();
    return () => { cancelled = true; };
  }, [showScheduleDetail, selectedSchedule?.id]);

  // 📤 Upload de photos/documents par le bureau sur une mission (ex: plan d'implantation)
  const handleBureauUploadMissionPhotos = async (event) => {
    const filesList = Array.from(event.target.files || []);
    event.target.value = '';
    if (!filesList.length || !selectedSchedule?.id) return;
    try {
      setUploadingMissionPhoto(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      filesList.forEach((file) => formData.append('files', file));
      formData.append('category', 'bureau_document');
      const res = await axios.post(
        `${API}/schedules/${selectedSchedule.id}/photos`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 30000 }
      );
      const newPhotos = res.data?.photos || [];
      setMissionPhotos((prev) => [...prev, ...newPhotos]);
    } catch (error) {
      console.error('Erreur upload photos mission (bureau):', error);
      const detail = error?.response?.data?.detail || "Impossible d'ajouter les photos";
      alert(detail);
    } finally {
      setUploadingMissionPhoto(false);
    }
  };

  // ⚡ Récupère les schedules parents référencés par des overrides
  // "postpone" mais absents de la liste courante (parent en dehors de la
  // fenêtre visible). Sans ça, le schedule virtuel reporté disparaît.
  useEffect(() => {
    const missingIds = Array.from(new Set(
      overrides
        .filter(o => o.override_type === 'postpone' && o.schedule_id)
        .map(o => o.schedule_id)
        .filter(id => !schedules.some(s => s.id === id))
    ));
    if (missingIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { 'Authorization': `Bearer ${token}` };
      try {
        // Pas d'endpoint GET-by-id : on utilise /schedules sans filtre de
        // dates et on filtre côté client par les IDs manquants.
        const res = await fetchPlanningSchedules(axios, `${API}/schedules`, { headers, timeout: 8000 });
        const all = res.data || [];
        const found = all.filter(s => missingIds.includes(s.id));
        if (!cancelled && found.length > 0) {
          setSchedules(prev => {
            const seen = new Set(prev.map(s => s.id));
            return [...prev, ...found.filter(s => !seen.has(s.id))];
          });
        }
      } catch (e) {
        console.warn('[planning] fetch missing parents failed:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [overrides, schedules]);

  // Ref pour éviter le stale closure dans le listener Realtime
  // Initialisée à null pour éviter la TDZ (loadPlanningData est défini plus bas)
  const loadPlanningDataRef = useRef(null);

  // ⚡ Realtime — merge instantané des changements reçus de Supabase
  useEffect(() => {
    const handler = (e) => {
      const { table, event, record } = e.detail || {};
      if (!record) return;

      if (table === 'schedules') {
        if (event === 'DELETE') {
          setSchedules(prev => prev.filter(s => s.id !== record.id));
        } else if (event === 'INSERT') {
          setSchedules(prev => {
            if (prev.some(s => s.id === record.id)) return prev;
            return [...prev, record];
          });
        } else {
          // UPDATE — merge les champs modifiés
          setSchedules(prev => prev.map(s => s.id === record.id ? { ...s, ...record } : s));
        }
      } else if (table === 'worksites') {
        if (event === 'DELETE') {
          setWorksites(prev => prev.filter(w => w.id !== record.id));
        } else if (event === 'INSERT') {
          setWorksites(prev => {
            if (prev.some(w => w.id === record.id)) return prev;
            return [...prev, record];
          });
        } else {
          setWorksites(prev => prev.map(w => w.id === record.id ? { ...w, ...record } : w));
        }
      }
    };
    window.addEventListener('realtime-update', handler);
    return () => window.removeEventListener('realtime-update', handler);
  }, []);

  const loadWorksiteAndOpenModal = async (worksiteId) => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API}/worksites/${worksiteId}`, { headers });
      const worksite = response.data;
      
      setSelectedWorksiteForPlanning(worksite);
      setPlanningData({
        team_leader_id: '',
        collaborator_ids: [],
        start_date: worksite.start_date || '',
        end_date: worksite.end_date || '',
        notes: '',
        shift_type: 'day'
      });
      setShowPlanningModal(true);
      
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
    } catch (error) {
      console.error('Erreur chargement chantier:', error);
    }
  };

  const loadPlanningData = async () => {
    const requestId = ++schedulesRequestId.current;
    try {
      perf.start('Planning.data');
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // ⚡ Calculer la fenêtre de dates visible pour filtrer côté serveur
      let fromDate, toDate;
      const d = currentDate;
      if (viewMode === 'month') {
        fromDate = new Date(d.getFullYear(), d.getMonth(), 1);
        toDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        fromDate.setDate(fromDate.getDate() - (fromDate.getDay() + 6) % 7);
        toDate.setDate(toDate.getDate() + (7 - toDate.getDay()) % 7);
      } else {
        // week / timeline / day — charger la semaine courante avec 1 jour de marge
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        fromDate = new Date(d);
        fromDate.setDate(diff - 1); // 1 jour avant le lundi
        toDate = new Date(fromDate);
        toDate.setDate(fromDate.getDate() + 9); // 1 jour après le dimanche
      }
      const pad = (n) => String(n).padStart(2, '0');
      const fromStr = `${fromDate.getFullYear()}-${pad(fromDate.getMonth()+1)}-${pad(fromDate.getDate())}`;
      const toStr = `${toDate.getFullYear()}-${pad(toDate.getMonth()+1)}-${pad(toDate.getDate())}`;

      // ⚡ Tous les appels en parallèle (au lieu de séquentiels)
      const [worksitesRes, leadersRes, usersRes, invitationsRes, schedulesRes] = await Promise.allSettled([
        axios.get(`${API}/worksites`, { headers }),
        axios.get(`${API}/team-leaders-stats`, { headers }),
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/invitations/accepted`, { headers }),
        fetchPlanningSchedules(axios, `${API}/schedules?from=${fromStr}&to=${toStr}`, { headers }),
      ]);

      // Chantiers
      if (worksitesRes.status === 'fulfilled') {
        setWorksites(worksitesRes.value.data || []);
      }

      // Chefs d'équipe
      if (leadersRes.status === 'fulfilled') {
        const transformedLeaders = (leadersRes.value.data || []).map((leader) => ({
          id: leader.id,
          nom: leader.last_name || 'Nom',
          prenom: leader.first_name || 'Prénom',
          email: leader.email || (leader.is_virtual ? '-' : 'Non renseigné'),
          telephone: leader.phone || '-',
          specialite: leader.specialty || 'Chef d\'équipe',
          couleur: leader.color || '#3B82F6',
          created_at: leader.created_at,
          is_virtual: leader.is_virtual,
          user_id: leader.user_id,
          collaborators: leader.collaborators || []
        }));
        setTeamLeaders(transformedLeaders);
        const teamCollabsMap = {};
        transformedLeaders.forEach(leader => {
          teamCollabsMap[leader.id] = leader.collaborators || [];
        });
        setTeamCollaborators(teamCollabsMap);
      } else {
        setTeamLeaders([]);
        setTeamCollaborators({});
      }

      // Collaborateurs (utilisateurs sauf FONDATEUR)
      if (usersRes.status === 'fulfilled') {
        setCollaborators((usersRes.value.data || []).filter(u => u.role !== 'FONDATEUR'));
      }

      // Invitations acceptées
      if (invitationsRes.status === 'fulfilled') {
        setAcceptedInvites(invitationsRes.value.data || []);
      }

      // Charger plannings + overrides ensemble pour éviter le flash visuel
      const ovRes = await axios.get(`${API}/planning-overrides`, { headers, timeout: 20000 });
      if (requestId !== schedulesRequestId.current) return;
      if (schedulesRes.status !== 'fulfilled') throw schedulesRes.reason;
      setSchedules(schedulesRes.value.data || []);
      setOverrides(ovRes.data || []);
      setPlanningError(false);

    } catch (error) {
      console.error('Erreur chargement planning:', error);
      if (requestId === schedulesRequestId.current) {
        setPlanningError(true);
        toast.error('Impossible de charger le planning.');
      }
    } finally {
      perf.end('Planning.data');
      if (requestId === schedulesRequestId.current) setLoading(false);
    }
  };

  // Mettre à jour la ref après chaque render pour éviter stale closure
  useEffect(() => {
    loadPlanningDataRef.current = loadPlanningData;
  });

  // ⚡ Lookup Maps O(1) — évite les .find() O(n) répétés dans le rendu
  const teamLeaderMap = useMemo(() => new Map(teamLeaders.map(tl => [tl.id, tl])), [teamLeaders]);
  const collaboratorMap = useMemo(() => new Map(collaborators.map(c => [c.id, c])), [collaborators]);
  const worksiteMap = useMemo(() => new Map(worksites.map(w => [w.id, w])), [worksites]);

  // ─── ASSIGNATION RAPIDE ───────────────────────────────────────────────
  const openAssignModal = (schedule, mode = 'single') => {
    if (mode === 'batch') {
      // Batch: assignTarget = { worksite_id, worksite_title, count }
      setAssignTarget(schedule);
    } else {
      setAssignTarget(schedule);
    }
    setAssignMode(mode);
    setAssignCollaboratorId('');
    setAssignTeamLeaderId('');
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    if (!assignCollaboratorId) { toast.error('Sélectionnez un technicien'); return; }
    setAssignLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      if (assignMode === 'batch' && assignTarget?.worksite_id) {
        // Batch assign via new endpoint
        const res = await axios.patch(
          `${API}/schedules/assign-batch/worksite/${assignTarget.worksite_id}`,
          { collaborator_id: assignCollaboratorId, team_leader_id: assignTeamLeaderId || undefined },
          { headers }
        );
        const count = res.data?.assigned || 0;
        toast.success(`${count} mission${count > 1 ? 's' : ''} assignée${count > 1 ? 's' : ''}`);
        // Update local state
        if (res.data?.schedules) {
          setSchedules(prev => prev.map(s => {
            const updated = res.data.schedules.find(u => u.id === s.id);
            return updated ? { ...s, ...updated } : s;
          }));
        }
      } else {
        // Single assign via PATCH
        const res = await axios.patch(
          `${API}/schedules/${assignTarget.id}`,
          { collaborator_id: assignCollaboratorId, status: 'scheduled', team_leader_id: assignTeamLeaderId || undefined },
          { headers }
        );
        toast.success('Mission assignée');
        setSchedules(prev => prev.map(s => s.id === assignTarget.id ? { ...s, ...res.data } : s));
      }
      setShowAssignModal(false);
      setAssignTarget(null);
    } catch (err) {
      toast.error('Erreur: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAssignLoading(false);
    }
  };

  // ─── DRAG & DROP ──────────────────────────────────────────────────────
  const handleDragStart = (e, schedule) => {
    setDraggedSchedule(schedule);
    e.dataTransfer.setData('text/plain', schedule.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnCollaborator = async (e, collaboratorId, teamLeaderId) => {
    e.preventDefault();
    const sched = draggedSchedule;
    setDraggedSchedule(null);
    if (!sched || sched.collaborator_id === collaboratorId) return;

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const res = await axios.patch(
        `${API}/schedules/${sched.id}`,
        { collaborator_id: collaboratorId, status: 'scheduled', team_leader_id: teamLeaderId || undefined },
        { headers }
      );
      toast.success('Mission assignée par glisser-déposer');
      setSchedules(prev => prev.map(s => s.id === sched.id ? { ...s, ...res.data } : s));
    } catch (err) {
      toast.error('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const loadAcceptedInvitations = async () => {
    try {
      const token = localStorage.getItem('token');
      
      try {
        const invitationsRes = await axios.get(`${API}/invitations/accepted`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setAcceptedInvites(invitationsRes.data);
      } catch (error) {
        console.error('Erreur chargement invitations acceptées:', error);
        // Sample accepted invitations data
        setAcceptedInvites([
          {
            id: '1',
            firstName: 'Marie',
            lastName: 'Dubois',
            email: 'marie.dubois@example.com',
            phone: '06.12.34.56.78',
            role: 'Technicien BTP',
            skills: 'Maçonnerie, Plomberie',
            accepted_at: '2025-01-10T10:00:00Z',
            usedAsTeamLeader: false,
            usedAsCollaborator: false
          },
          {
            id: '2',
            firstName: 'Pierre',
            lastName: 'Martin',
            email: 'pierre.martin@example.com',
            phone: '06.98.76.54.32',
            role: 'Chef de chantier',
            skills: 'Électricité, Coordination équipe',
            accepted_at: '2025-01-08T14:30:00Z',
            usedAsTeamLeader: false,
            usedAsCollaborator: false
          }
        ]);
      }
      
      // Keep the old invitations data for backward compatibility
      const acceptedInvitations = [
        {
          id: 'inv_manager_bureau',
          email: 'manager@bureau.fr',
          nom: 'Manager',
          prenom: 'Bureau',
          role: 'BUREAU',
          status: 'ACCEPTED',
          specialite: 'Gestion de projet',
          competences: 'Management, Planning, Coordination équipes',
          accepted_at: '2024-01-11T09:15:00Z',
          invited_at: '2024-01-10T14:30:00Z'
        },
        {
          id: 'inv_nouveau_tech',
          email: 'nouveau@technicien.fr',
          nom: 'Nouveau',
          prenom: 'Technicien',
          role: 'TECHNICIEN',
          status: 'ACCEPTED',
          specialite: 'Travaux terrain',
          competences: 'Recherche terrain, Mesures, Diagnostics',
          accepted_at: '2024-01-12T10:45:00Z',
          invited_at: '2024-01-15T10:00:00Z'
        }
      ];
      
      setInvitations(acceptedInvitations.filter(inv => inv.status === 'ACCEPTED'));
      console.log('Invitations acceptées chargées:', acceptedInvitations.length);
    } catch (error) {
      console.error('Erreur chargement invitations:', error);
      setInvitations([]);
      setAcceptedInvites([]);
    }
  };

  // Fonctions utilitaires pour les vues temporelles
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const getWeekDates = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Lundi comme premier jour
    startOfWeek.setDate(diff);
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startOfWeek);
      currentDate.setDate(startOfWeek.getDate() + i);
      weekDates.push(currentDate);
    }
    return weekDates;
  };

  // Dédupliquer les schedules par chantier pour afficher une seule carte par mission
  const deduplicateTeamSchedules = (teamSchedules, weekStart) => {
    const weekStartDate = new Date(weekStart);
    weekStartDate.setHours(0, 0, 0, 0);

    // Grouper par worksite_id + dates de début/fin pour dédupliquer
    const scheduleMap = new Map();

    teamSchedules.forEach(schedule => {
      // Support des deux formats: period_start/period_end OU start_date/end_date
      const key = `${planningGroupKey(schedule)}_${schedule.status}_${Boolean(schedule._postponed_placeholder)}_${Boolean(schedule._isVirtualPostpone)}`;
      
      if (!scheduleMap.has(key)) {
        scheduleMap.set(key, { ...schedule, _ids: [schedule.id] });
      } else {
        scheduleMap.get(key)._ids.push(schedule.id);
      }
    });

    // Convertir en array et calculer les positions grille
    const grouped = [];
    scheduleMap.forEach(schedule => {
      // Utiliser period_start/period_end en priorité, sinon start_date/end_date
      const startDateStr = schedule.start_date || schedule.period_start || schedule.date;
      const endDateStr = schedule.end_date || schedule.period_end || startDateStr;

      if (startDateStr && endDateStr) {
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        const daysDiff = Math.round((startDate.getTime() - weekStartDate.getTime()) / (1000 * 60 * 60 * 24));
        const daysSpan = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const columnStart = Math.max(1, daysDiff + 1);
        const columnEnd = Math.min(8, daysDiff + 1 + daysSpan);
        const columnSpan = columnEnd - columnStart;

        if (columnSpan > 0) {
          grouped.push({
            ...schedule,
            _start: startDate,
            _end: endDate,
            _ids: schedule._ids,
            columnStart,
            columnSpan,
            isMultiDay: daysSpan > 1,
            dayCount: daysSpan
          });
        }
      }
    });

    return grouped;
  };

  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Commencer par le lundi de la première semaine
    const startDate = new Date(firstDay);
    const startDay = firstDay.getDay();
    startDate.setDate(firstDay.getDate() - (startDay === 0 ? 6 : startDay - 1));
    
    const monthDates = [];
    const current = new Date(startDate);
    
    while (current <= lastDay || monthDates.length % 7 !== 0) {
      monthDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return monthDates;
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week' || viewMode === 'timeline') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      const originalDay = newDate.getDate();
      newDate.setDate(1);
      newDate.setMonth(newDate.getMonth() + direction);
      newDate.setDate(Math.min(originalDay, new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate()));
    }
    setCurrentDate(newDate);
  };

  // NEW: Enhanced day detail function
  const openDayDetail = (date) => {
    const daySchedules = getSchedulesForDate(date);
    const dayTeams = teamLeaders.filter(leader => {
      // Logic to check if team leader is assigned to this day
      return daySchedules.some(schedule => schedule.team_leader_id === leader.id);
    });
    
    const dayCollaborators = collaborators.filter(collab => {
      // Logic to check if collaborator is assigned to this day
      return daySchedules.some(schedule => schedule.collaborator_id === collab.id || schedule.collaborator_id === collab.user_id);
    });

    setSelectedDayData({
      date: date,
      schedules: daySchedules,
      teams: dayTeams,
      collaborators: dayCollaborators,
      totalHours: daySchedules.reduce((sum, schedule) => sum + (Number(schedule.hours) || 0), 0),
      availableSlots: 8 - daySchedules.length // Assuming 8 slots per day
    });
    setDetailedDate(date);
    // Format local (toISOString décale d'un jour en UTC+2)
    setSelectedDate(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
    setShowDayDetail(true);
  };

  // ⚡ Mémo des schedules avec overrides appliqués (évite recalcul à chaque render)
  const effectiveSchedulesData = useMemo(() => {
    const postponeOvs = expandPlanningOverrides(schedules, overrides);
    const postponedByScheduleId = {};
    postponeOvs.forEach(o => {
      if (!postponedByScheduleId[o.schedule_id]) postponedByScheduleId[o.schedule_id] = [];
      postponedByScheduleId[o.schedule_id].push(o);
    });
    const virtualSchedules = postponeOvs
      .filter(o => o.override_type === 'postpone' && o.new_date)
      .map(o => {
        const base = schedules.find(s => s.id === o.schedule_id);
        if (!base) return null;
        // ⚠️ Forcer un statut actif pour le virtuel : sinon, si le parent passe
        // en `partially_completed` (état final), le filtre FINAL_STATUSES cache
        // la journée reportée et elle "disparaît" du planning du 11.
        const baseStatus = (base.status || '').toLowerCase();
        const FINAL = ['completed', 'cancelled', 'partially_completed'];
        const safeStatus = baseStatus || 'scheduled';
        return {
          ...base,
          status: safeStatus,
          start_date: o.new_date,
          end_date: o.new_date,
          period_start: null,
          period_end: null,
          date: o.new_date,
          _override_id: o.id,
          _isVirtualPostpone: true,
          _originalDate: o.original_date,
          _segKey: `${base.id}_virt_${o.id}`,
        };
      })
      .filter(Boolean);
    const placeholderSchedules = postponeOvs.map(o => {
      const base = schedules.find(s => s.id === o.schedule_id);
      if (!base) return null;
      const baseStatus = (base.status || '').toLowerCase();
      const FINAL = ['completed', 'cancelled', 'partially_completed'];
      const safeStatus = baseStatus || 'scheduled';
      return {
        ...base,
        status: safeStatus,
        id: `${base.id}_postponed_${o.original_date}_${o.id}`,
        start_date: o.original_date,
        end_date: o.original_date,
        period_start: null,
        period_end: null,
        date: o.original_date,
        _postponed_placeholder: true,
        _override_id: o.id,
      };
    }).filter(Boolean);
    const splitSchedules = [];
    // Format local YYYY-MM-DD : toISOString() repasse en UTC et décale d'un jour
    // les dates locales à minuit (UTC+2 en été) → segments fantômes la veille
    const fmtLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    schedules.forEach(s => {
      const sd = s.start_date || s.date;
      const ed = s.end_date || sd;
      const ovs = postponedByScheduleId[s.id] || [];
      const postponedDates = new Set(ovs.map(o => o.original_date));
      if (postponedDates.size === 0 || !sd) {
        splitSchedules.push(s);
        return;
      }
      const startD = new Date(sd + 'T00:00:00');
      const endD = new Date(ed + 'T00:00:00');
      let segStart = null;
      const flush = (segEnd) => {
        if (!segStart) return;
        const segStartStr = fmtLocalDate(segStart);
        const segEndStr = fmtLocalDate(segEnd);
        splitSchedules.push({ ...s, start_date: segStartStr, end_date: segEndStr, period_start: null, period_end: null, date: segStartStr, _segKey: `${s.id}_seg_${segStartStr}` });
        segStart = null;
      };
      for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
        const dStr = fmtLocalDate(d);
        if (postponedDates.has(dStr)) {
          if (segStart) {
            const prev = new Date(d); prev.setDate(prev.getDate() - 1);
            flush(prev);
          }
        } else {
          if (!segStart) segStart = new Date(d);
        }
      }
      if (segStart) flush(endD);
    });
    return [...splitSchedules, ...virtualSchedules, ...placeholderSchedules];
  }, [schedules, overrides]);

  const getSchedulesForDate = (date) => {
    // Utiliser les méthodes locales pour éviter les problèmes de timezone
    let dateStr;
    if (typeof date === 'string') {
      dateStr = date;
    } else {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }

    return effectiveSchedulesData.filter(schedule => {
      if (schedule._postponed_placeholder || schedule.status === 'cancelled') return false;
      const startDate = schedule.start_date || schedule.date;
      const endDate = schedule.end_date || schedule.date;
      if (!startDate) return false;
      const end = endDate || startDate;
      return dateStr >= startDate && dateStr <= end;
    });
  };

  const getDateTitle = () => {
    if (viewMode === 'week' || viewMode === 'timeline') {
      const weekDates = getWeekDates(currentDate);
      const startDate = weekDates[0];
      const endDate = weekDates[6];
      return `Semaine du ${startDate.getDate()}/${startDate.getMonth() + 1} au ${endDate.getDate()}/${endDate.getMonth() + 1}/${endDate.getFullYear()}`;
    } else if (viewMode === 'month') {
      return new Intl.DateTimeFormat('fr-FR', { 
        year: 'numeric', 
        month: 'long' 
      }).format(currentDate);
    }
    return '';
  };

  const addInvitedPersonAsTeamLeader = async (invitation) => {
    const teamLeaderData = {
      nom: invitation.lastName,
      prenom: invitation.firstName,
      email: invitation.email,
      telephone: invitation.phone || '',
      specialite: invitation.skills || 'Généraliste',
      couleur: '#10B981' // Green color for invited persons
    };

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/team-leaders`, teamLeaderData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setTeamLeaders(prev => [...prev, response.data]);
      
      // Mark invitation as used for team leader
      const updatedInvitation = { ...invitation, usedAsTeamLeader: true };
      setAcceptedInvites(prev => prev.map(inv => 
        inv.id === invitation.id ? updatedInvitation : inv
      ));

      toast.success(`${invitation.firstName} ${invitation.lastName} a été ajouté(e) comme chef d'équipe !`);
    } catch (error) {
      console.error('Erreur ajout chef d\'équipe:', error);
      // Fallback
      const newTeamLeader = {
        id: `invited-${Date.now()}`,
        ...teamLeaderData,
        created_at: new Date().toISOString(),
        fromInvitation: true,
        invitationId: invitation.id
      };

      setTeamLeaders(prev => [...prev, newTeamLeader]);
      
      // Mark invitation as used for team leader
      const updatedInvitation = { ...invitation, usedAsTeamLeader: true };
      setAcceptedInvites(prev => prev.map(inv => 
        inv.id === invitation.id ? updatedInvitation : inv
      ));

      toast.success(`${invitation.firstName} ${invitation.lastName} a été ajouté(e) comme chef d'équipe !`);
    }
  };

  const addInvitedPersonAsCollaborator = async (invitation) => {
    if (teamLeaders.length === 0) {
      toast.warning('Veuillez d\'abord ajouter un chef d\'équipe avant d\'ajouter des collaborateurs.');
      return;
    }

    const collaboratorData = {
      nom: invitation.lastName,
      prenom: invitation.firstName,
      email: invitation.email,
      telephone: invitation.phone || '',
      competences: invitation.skills || 'Généraliste',
      team_leader_id: teamLeaders[0].id // Assign to first team leader for now
    };

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/collaborators`, collaboratorData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setCollaborators(prev => [...prev, response.data]);
      
      // Mark invitation as used for collaborator
      const updatedInvitation = { ...invitation, usedAsCollaborator: true };
      setAcceptedInvites(prev => prev.map(inv => 
        inv.id === invitation.id ? updatedInvitation : inv
      ));

      toast.success(`${invitation.firstName} ${invitation.lastName} a été ajouté(e) comme collaborateur !`);
    } catch (error) {
      console.error('Erreur ajout collaborateur:', error);
      // Fallback
      const newCollaborator = {
        id: `invited-collab-${Date.now()}`,
        ...collaboratorData,
        created_at: new Date().toISOString(),
        fromInvitation: true,
        invitationId: invitation.id
      };

      setCollaborators(prev => [...prev, newCollaborator]);
      
      // Mark invitation as used for collaborator
      const updatedInvitation = { ...invitation, usedAsCollaborator: true };
      setAcceptedInvites(prev => prev.map(inv => 
        inv.id === invitation.id ? updatedInvitation : inv
      ));

      toast.success(`${invitation.firstName} ${invitation.lastName} a été ajouté(e) comme collaborateur !`);
    }
  };

  const addTeamLeader = async () => {
    try {
      if (!teamLeaderData.nom || !teamLeaderData.prenom) {
        toast.warning('Nom et prénom sont obligatoires');
        return;
      }

      const token = localStorage.getItem('token');
      
      // Préparer les données pour l'API
      const apiData = {
        first_name: teamLeaderData.prenom,
        last_name: teamLeaderData.nom,
        phone: teamLeaderData.telephone,
        specialty: teamLeaderData.specialite,
        color: teamLeaderData.couleur
      };
      
      if (selectedExistingUser) {
        // Cas 1: Ajouter un utilisateur existant comme chef d'équipe
        apiData.user_id = selectedExistingUser;
        
        // Récupérer les informations de l'utilisateur sélectionné pour afficher son rôle
        const selectedUser = collaborators.find(c => c.id === selectedExistingUser);
        const userRole = selectedUser?.role || 'utilisateur';
        const roleLabels = {
          'ADMIN': 'administrateur',
          'BUREAU': 'bureau',
          'TECHNICIEN': 'technicien'
        };
        const roleLabel = roleLabels[userRole] || userRole.toLowerCase();
        
        toast.success(`Membre ajouté comme chef d'équipe dans le planning ! (accès ${roleLabel} conservé)`);
      } else {
        // Cas 2: Créer un chef d'équipe fictif
        toast.success('Chef d\'équipe ajouté au planning avec succès !');
      }
      
      // Envoyer à l'API
      const response = await axios.post(`${API}/team-leaders`, apiData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Réinitialiser le formulaire
      setTeamLeaderData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        specialite: '',
        couleur: '#3B82F6'
      });
      setSelectedExistingUser(null);
      setShowTeamLeaderForm(false);
    } catch (error) {
      console.error('Erreur ajout chef d\'équipe:', error);
      toast.error('Erreur lors de l\'ajout du chef d\'équipe: ' + (error.response?.data?.detail || error.message));
    }
  };

  const addCollaborator = async () => {
    console.log('🚀 [Collaborateur] Début création collaborateur');
    console.log('📋 [Collaborateur] Données formulaire:', collaboratorData);
    
    try {
      if (!collaboratorData.nom || !collaboratorData.prenom) {
        console.warn('⚠️ [Collaborateur] Validation échouée: nom ou prénom manquant');
        toast.warning('Nom et prénom sont obligatoires');
        return;
      }

      const token = localStorage.getItem('token');
      console.log('🔑 [Collaborateur] Token récupéré:', token ? 'OK' : 'MANQUANT');
      
      // Créer un utilisateur avec rôle TECHNICIEN (email temporaire généré automatiquement)
      const userData = {
        first_name: collaboratorData.prenom,
        last_name: collaboratorData.nom,
        email: `${collaboratorData.prenom.toLowerCase()}.${collaboratorData.nom.toLowerCase()}@temp-skyapp.local`,
        phone: collaboratorData.telephone || '',
        role: 'TECHNICIEN'
      };
      
      // Ajouter skills seulement si renseigné
      if (collaboratorData.competences && collaboratorData.competences.trim()) {
        userData.skills = collaboratorData.competences.trim();
      }

      console.log('📤 [Collaborateur] Envoi POST /api/users avec:', userData);
      const response = await axios.post(`${API}/users`, userData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const newUser = response.data;
      console.log('✅ [Collaborateur] Utilisateur créé:', newUser);
      
      // Si un chef d'équipe est sélectionné, l'assigner
      if (collaboratorData.team_leader_id) {
        console.log('👔 [Collaborateur] Assignation au chef d\'équipe:', collaboratorData.team_leader_id);
        try {
          const assignData = {
            team_leader_id: collaboratorData.team_leader_id,
            collaborator_id: newUser.id,
            notes: collaboratorData.competences || ''
          };
          console.log('📤 [Collaborateur] Envoi POST /api/team-leaders/assign avec:', assignData);
          await axios.post(`${API}/team-leaders/assign`, assignData, { 
            headers: { 'Authorization': `Bearer ${token}` } 
          });
          console.log('✅ [Collaborateur] Assignation réussie');
        } catch (assignError) {
          console.error('❌ [Collaborateur] Erreur assignation:', assignError);
          console.warn('⚠️ [Collaborateur] Continue malgré l\'erreur d\'assignation');
        }
      } else {
        console.log('ℹ️ [Collaborateur] Pas de chef d\'équipe sélectionné, collaborateur non assigné');
      }

      // Ajouter le nouveau collaborateur à l'état local (optimiste)
      setCollaborators(prev => [...prev, newUser]);
      console.log('🎉 [Collaborateur] Création terminée avec succès !');
      setCollaboratorData({
        nom: '',
        prenom: '',
        telephone: '',
        competences: '',
        team_leader_id: ''
      });
      setShowCollaboratorForm(false);
      toast.success('Collaborateur ajouté avec succès !');
    } catch (error) {
      console.error('❌ [Collaborateur] ERREUR lors de la création:', error);
      console.error('❌ [Collaborateur] Status:', error.response?.status);
      console.error('❌ [Collaborateur] Détails:', error.response?.data);
      console.error('❌ [Collaborateur] Message:', error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      toast.error('Erreur: ' + errorMsg);
    }
  };

  const addSchedule = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Validation selon la catégorie
      if (scheduleData.intervention_category === 'worksite') {
        if (scheduleData.worksite_mode === 'existing' && !scheduleData.worksite_id) {
          toast.warning('Veuillez sélectionner un chantier');
          return;
        }
        if (scheduleData.worksite_mode === 'new' && !scheduleData.new_worksite_title) {
          toast.warning('Veuillez entrer un nom pour le nouveau chantier');
          return;
        }
        if (!scheduleData.team_leader_id || !scheduleData.collaborator_id) {
          toast.warning('Chef d\'équipe et collaborateur sont obligatoires');
          return;
        }
      } else if (scheduleData.intervention_category === 'urgence') {
        if (!scheduleData.client_name || !scheduleData.team_leader_id || !scheduleData.collaborator_id) {
          toast.warning('Nom du client, chef d\'équipe et collaborateur sont obligatoires pour les urgences');
          return;
        }
      } else if (scheduleData.intervention_category === 'rdv') {
        if (!scheduleData.client_name) {
          toast.warning('Nom du client est obligatoire');
          return;
        }
      }

      // Si nouveau chantier, le créer d'abord
      let worksiteId = scheduleData.worksite_id;
      if (scheduleData.intervention_category === 'worksite' && scheduleData.worksite_mode === 'new') {
        const worksiteRes = await axios.post(`${API}/worksites`, {
          title: scheduleData.new_worksite_title,
          address: scheduleData.new_worksite_address || '',
          status: 'PLANNED',
          source: 'MANUAL',
          start_date: scheduleData.start_date,
          end_date: scheduleData.end_date
        }, { headers });
        worksiteId = worksiteRes.data?.id;
        if (!worksiteId) {
          toast.error('Erreur lors de la création du chantier');
          return;
        }
      }

      const selectedShift = SHIFT_OPTIONS.find(s => s.value === scheduleData.shift);

      // Tous les membres à planifier : chef d'équipe + collaborateur principal + supplémentaires
      const allCollaborators = [
        scheduleData.team_leader_id, 
        scheduleData.collaborator_id, 
        ...scheduleData.additional_collaborators
      ].filter(Boolean); // Enlever les valeurs vides

      // Créer UN schedule par collaborateur avec period_start/period_end
      const promises = [];
      for (const collabId of allCollaborators) {
        const schedulePayload = {
          worksite_id: worksiteId || undefined,
          team_leader_id: scheduleData.team_leader_id || undefined,
          collaborator_id: collabId,
          intervention_category: scheduleData.intervention_category || 'worksite',
          period_start: scheduleData.start_date,
          period_end: scheduleData.end_date,
          time: scheduleData.time,
          shift: scheduleData.shift,
          hours: selectedShift?.hours || 8,
          description: scheduleData.description || '',
          client_name: scheduleData.client_name || undefined,
          client_address: scheduleData.client_address || undefined
        };
        console.log('📤 Payload envoyé au backend:', schedulePayload);
        promises.push(axios.post(`${API}/schedules`, schedulePayload, { headers }));
      }

      const results = await Promise.allSettled(promises);
      const succeeded = results.filter(r => r.status === 'fulfilled');
      const duplicates = results.filter(r => r.status === 'rejected' && r.reason?.response?.status === 409);
      const errors = results.filter(r => r.status === 'rejected' && r.reason?.response?.status !== 409);

      // Log des erreurs pour debugging
      if (errors.length > 0) {
        console.error('❌ Erreurs lors de la création des schedules:');
        errors.forEach((err, idx) => {
          console.error(`  Erreur ${idx + 1}:`, err.reason?.response?.data);
          console.error(`  Status:`, err.reason?.response?.status);
        });
      }

      if (succeeded.length === 0 && duplicates.length > 0) {
        toast.warning('Tous les plannings existent déjà pour ces dates !');
        return;
      }

      if (succeeded.length === 0 && errors.length > 0) {
        toast.error('Erreur lors de la création des plannings. Voir la console pour les détails.');
        return;
      }

      // Mise à jour optimiste : ajouter les schedules créés à l'état local immédiatement
      // (déduplication par id : le Realtime peut déjà les avoir insérés)
      const newSchedules = succeeded.map(r => r.value.data);
      setSchedules(prev => {
        const seen = new Set(prev.map(s => s.id));
        return [...prev, ...newSchedules.filter(s => s && !seen.has(s.id))];
      });

      // Si nouveau chantier, l'ajouter optimistement à la liste
      if (scheduleData.worksite_mode === 'new') {
        try {
          const worksitesRes = await axios.get(`${API}/worksites`, { headers });
          setWorksites(worksitesRes.data || []);
        } catch(e) {}
      }

      const resetState = {
        intervention_category: 'worksite',
        worksite_mode: 'existing',
        worksite_id: '',
        new_worksite_title: '',
        new_worksite_address: '',
        client_name: '',
        client_address: '',
        client_contact: '',
        team_leader_id: '',
        collaborator_id: '',
        additional_collaborators: [],
        start_date: selectedDate,
        end_date: selectedDate,
        time: '08:00',
        shift: 'day',
        hours: '8',
        description: ''
      };
      setScheduleData(resetState);
      setShowScheduleForm(false);

      if (duplicates.length > 0) {
        toast.success(`${succeeded.length} planning(s) créé(s) ! (${duplicates.length} doublons ignorés)`);
      } else {
        toast.success(`${succeeded.length} planning(s) créé(s) avec succès !`);
      }
    } catch (error) {
      console.error('Erreur ajout planning:', error);
      toast.error('Erreur lors de la création du planning: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const removeTeamLeader = async (id) => {
    if (await confirm('Êtes-vous sûr de vouloir supprimer ce chef d\'équipe ?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/team-leaders/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setTeamLeaders(prev => prev.filter(tl => tl.id !== id));
      } catch (error) {
        console.error('Erreur suppression chef d\'équipe:', error);
        // Fallback en cas d'erreur avec l'API
        setTeamLeaders(prev => prev.filter(tl => tl.id !== id));
      }
    }
  };

  const removeCollaborator = async (id) => {
    if (await confirm('Êtes-vous sûr de vouloir supprimer ce collaborateur ?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/users/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setCollaborators(prev => prev.filter(c => c.id !== id));
        toast.success('Collaborateur supprimé avec succès');
      } catch (error) {
        console.error('Erreur suppression collaborateur:', error);
        toast.error('Erreur lors de la suppression: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const deleteSchedule = async (id) => {
    if (await confirm('Êtes-vous sûr de vouloir supprimer ce planning ?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/schedules/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSchedules(prev => prev.filter(s => s.id !== id));
        toast.success('Planning supprimé avec succès');
      } catch (error) {
        console.error('Erreur suppression planning:', error);
        toast.error('Erreur lors de la suppression: ' + (error.response?.data?.detail || error.message));
      }
    }
  };
  
  const openEditCollaboratorModal = (collaborator) => {
    setEditingCollaborator(collaborator);
    setEditCollaboratorData({
      first_name: collaborator.first_name || '',
      last_name: collaborator.last_name || '',
      phone: collaborator.phone || ''
    });
    setShowEditCollaboratorModal(true);
  };
  
  const handleEditCollaborator = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/users/${editingCollaborator.id}`,
        editCollaboratorData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Mise à jour optimiste du collaborateur modifié
      setCollaborators(prev => prev.map(c =>
        c.id === editingCollaborator.id ? { ...c, ...editCollaboratorData } : c
      ));
      setShowEditCollaboratorModal(false);
      setEditingCollaborator(null);
      toast.success('Collaborateur modifié avec succès !');
    } catch (error) {
      console.error('Erreur modification collaborateur:', error);
      toast.error('Erreur lors de la modification: ' + (error.response?.data?.detail || error.message));
    }
  };

  const removeSchedule = async (id) => {
    if (await confirm('Êtes-vous sûr de vouloir supprimer ce planning ?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/schedules/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSchedules(prev => prev.filter(s => s.id !== id));
      } catch (error) {
        console.error('Erreur suppression planning:', error);
        // Fallback en cas d'erreur avec l'API
        setSchedules(prev => prev.filter(s => s.id !== id));
      }
    }
  };

  const getTeamLeaderName = (id) => {
    const leader = teamLeaderMap.get(id);
    return leader ? `${leader.prenom} ${leader.nom}` : 'Inconnu';
  };

  const getCollaboratorName = (id) => {
    const collaborator = collaboratorMap.get(id);
    if (!collaborator) return 'Inconnu';
    
    // Gérer les différents formats de nom possibles
    const prenom = collaborator.prenom || collaborator.first_name || '';
    const nom = collaborator.nom || collaborator.last_name || collaborator.name || '';
    
    if (prenom && nom) {
      return `${prenom} ${nom}`;
    } else if (collaborator.email) {
      return collaborator.email.split('@')[0]; // Utiliser la partie avant @ de l'email
    }
    
    return 'Inconnu';
  };

  const getWorksiteName = (id) => {
    const worksite = worksiteMap.get(id);
    return worksite ? worksite.title : 'Chantier inconnu';
  };

  const getShiftLabel = (shift) => {
    const shiftOption = SHIFT_OPTIONS.find(s => s.value === shift);
    return shiftOption ? shiftOption.label : shift;
  };

  const getTeamMembersDisplay = (teamLeaderId, selectedCollaboratorId) => {
    if (!teamLeaderId) return 'Aucun chef d\'équipe sélectionné';
    
    const leader = teamLeaderMap.get(teamLeaderId);
    if (!leader) return 'Chef d\'équipe introuvable';
    
    const leaderName = `${leader.prenom} ${leader.nom}`;
    
    // Récupérer les collaborateurs de ce chef depuis teamCollaborators (chargés via API)
    const teamMembers = teamCollaborators[teamLeaderId] || [];
    
    console.log(`👥 Affichage équipe pour ${leaderName}:`, teamMembers);
    
    if (teamMembers.length === 0) {
      return `${leaderName} (Chef - aucun collaborateur)`;
    }
    
    // Construire la liste des noms
    const collaboratorNames = teamMembers.map(item => {
      // L'API peut retourner { collaborator: { id, first_name, ... } } ou directement { id, first_name, ... }
      const c = item.collaborator || item;
      const prenom = c.prenom || c.first_name || '';
      const nom = c.nom || c.last_name || c.name || '';
      const displayName = prenom && nom ? `${prenom} ${nom}` : (c.email ? c.email.split('@')[0] : 'Inconnu');
      console.log(`  - Membre: ${displayName}`, c);
      return displayName;
    });
    
    return `${leaderName} (Chef) + ${collaboratorNames.join(', ')}`;
  };

  const getTeamCollaboratorsList = (teamLeaderId) => {
    if (!teamLeaderId) return 'Aucun';
    
    const teamMembers = teamCollaborators[teamLeaderId] || [];
    
    if (teamMembers.length === 0) {
      return 'Aucun collaborateur';
    }
    
    const collaboratorNames = teamMembers.map(item => {
      const c = item.collaborator || item;
      const prenom = c.prenom || c.first_name || '';
      const nom = c.nom || c.last_name || c.name || '';
      return prenom && nom ? `${prenom} ${nom}` : (c.email ? c.email.split('@')[0] : 'Inconnu');
    });
    
    return collaboratorNames.join(', ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563EB] mx-auto"></div>
          <p className="mt-4 text-[#64748B]">Chargement du planning...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {planningError && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 border-l-4 border-red-600 bg-red-50 p-3 text-sm text-red-900">
          <span>Planning non actualisé. Les données affichées peuvent être incomplètes.</span>
          <Button onClick={() => loadPlanningData()} variant="outline">Recharger</Button>
        </div>
      )}
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-0 shadow-2xl overflow-hidden">
        {/* Bandeau gradient SkyApp */}
        <div className="p-4 sm:p-6 lg:p-8 text-white" style={{background:'linear-gradient(135deg,#2563EB 0%,#7C3AED 55%,#FB923C 100%)'}}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">Gestion du Planning</h2>
              <p className="text-xs sm:text-sm text-blue-100 mt-1 hidden sm:block">Équipes, collaborateurs et planification des chantiers</p>
            </div>
            <div className="flex flex-wrap gap-2 sm:space-x-3 w-full sm:w-auto">
              <Button
                onClick={() => setShowTeamLeaderForm(true)}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border border-white/40 rounded-xl sm:rounded-2xl px-3 py-2 text-sm flex-1 sm:flex-initial shadow-sm transition-all"
              >
                <UserPlus className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">+ Chef d'équipe</span>
                <span className="sm:hidden">+ Chef</span>
              </Button>
              <Button
                onClick={() => setShowCollaboratorForm(true)}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border border-white/40 rounded-xl sm:rounded-2xl px-3 py-2 text-sm flex-1 sm:flex-initial shadow-sm transition-all"
              >
                <Users className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">+ Collaborateur</span>
                <span className="sm:hidden">+ Collab.</span>
              </Button>
              <Button
                onClick={() => setShowScheduleForm(true)}
                className="bg-white text-[#7C3AED] hover:bg-blue-50 rounded-xl sm:rounded-2xl px-3 py-2 text-sm flex-1 sm:flex-initial shadow-sm font-semibold transition-all"
              >
                <Calendar className="h-4 w-4 mr-1 sm:mr-2" />
                + Planning
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs améliorés avec badges et icônes */}
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex overflow-x-auto space-x-1 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl p-1.5 shadow-inner border border-gray-200 scrollbar-hide">
            <button
              onClick={() => setActiveTab('planning')}
              className={`flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-200 whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'planning' 
                  ? 'bg-white text-[#2563EB] font-semibold shadow-lg transform scale-105' 
                  : 'text-[#64748B] hover:text-[#1e3a8a] hover:bg-white/60'
              }`}
            >
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Planning</span>
              <span className={`ml-1 px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-bold ${
                activeTab === 'planning'
                  ? 'bg-[#2563EB] text-white'
                  : 'bg-slate-200 text-[#64748B]'
              }`}>
                {schedules.filter(s => !['completed','cancelled','partially_completed'].includes(s.status)).length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('teams')}
              className={`flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-200 whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'teams' 
                  ? 'bg-white text-[#2563EB] font-semibold shadow-lg transform scale-105' 
                  : 'text-[#64748B] hover:text-[#1e3a8a] hover:bg-white/60'
              }`}
            >
              <Users className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Équipes</span>
              <span className={`ml-1 px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-bold ${
                activeTab === 'teams'
                  ? 'bg-[#2563EB] text-white'
                  : 'bg-slate-200 text-[#64748B]'
              }`}>
                {teamLeaders.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('collaborators')}
              className={`flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-200 whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'collaborators' 
                  ? 'bg-white text-[#7C3AED] font-semibold shadow-lg transform scale-105' 
                  : 'text-[#64748B] hover:text-[#6d28d9] hover:bg-white/60'
              }`}
            >
              <UserPlus className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Collaborateurs</span>
              <span className="sm:hidden">Collabs</span>
              <span className={`ml-1 px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-bold ${
                activeTab === 'collaborators'
                  ? 'bg-[#7C3AED] text-white'
                  : 'bg-slate-200 text-[#64748B]'
              }`}>
                {collaborators.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('interventions')}
              className={`flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-200 whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'interventions' 
                  ? 'bg-white text-[#FB923C] font-semibold shadow-lg transform scale-105' 
                  : 'text-[#64748B] hover:text-[#1e3a8a] hover:bg-white/60'
              }`}
            >
              <Pause className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Interventions</span>
              <span className="sm:hidden">Interv.</span>
              <span className={`ml-1 px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-bold ${
                activeTab === 'interventions'
                  ? 'bg-[#FB923C] text-white'
                  : 'bg-slate-200 text-[#64748B]'
              }`}>
                {schedules.filter(s => ['paused','cancelled','completed','partially_completed'].includes(s.status)).length + overrides.length}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Outils de Visualisation Temporelle - Visible uniquement dans l'onglet Planning */}
      {activeTab === 'planning' && (
        <Card className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-0 shadow-lg">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
              {/* Sélecteur de Vue amélioré */}
              <div className="w-full lg:w-auto">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                  <CalendarRange className="h-4 w-4 sm:h-5 sm:w-5 text-[#2563EB]" />
                  <span className="hidden sm:inline">Visualisation du Planning</span>
                  <span className="sm:hidden">Planning</span>
                </h3>
                <div className="flex space-x-2 sm:space-x-3">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-5 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-200 shadow-sm text-sm sm:text-base ${
                    viewMode === 'timeline' 
                      ? 'bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white shadow-lg transform scale-105' 
                      : 'bg-white text-[#475569] hover:bg-slate-50 border border-[rgba(15,23,42,0.12)] hover:border-blue-200'
                  }`}
                >
                  <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">Planning Équipe</span>
                  <span className="sm:hidden">Équipe</span>
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-5 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-200 shadow-sm text-sm sm:text-base ${
                    viewMode === 'month' 
                      ? 'bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white shadow-lg transform scale-105' 
                      : 'bg-white text-[#475569] hover:bg-slate-50 border border-[rgba(15,23,42,0.12)] hover:border-blue-200'
                  }`}
                >
                  <CalendarRange className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Mois</span>
                </button>
              </div>
            </div>

            {/* Navigation Temporelle améliorée */}
            <div className="flex items-center justify-between space-x-2 sm:space-x-4 w-full">
              <div className="flex items-center space-x-2 sm:space-x-3 flex-1">
                {/* Bouton Précédent */}
                <button
                  onClick={() => navigateDate(-1)}
                  className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-blue-50 hover:bg-blue-100 text-[#2563EB] hover:text-[#1d4ed8] transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 flex-shrink-0"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                
                {/* Date actuelle avec style amélioré */}
                <div className="px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg sm:rounded-xl border-2 border-gray-200 shadow-sm flex-1 min-w-0">
                  <span className="text-xs sm:text-base font-bold text-gray-800 truncate block">{getDateTitle()}</span>
                </div>
                
                {/* Bouton Suivant */}
                <button
                  onClick={() => navigateDate(1)}
                  className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-blue-50 hover:bg-blue-100 text-[#2563EB] hover:text-[#1d4ed8] transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 flex-shrink-0"
                >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
              
              {/* Bouton Aujourd'hui avec badge */}
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setCurrentDate(new Date())}
                  className="flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-[#2563EB] to-[#7C3AED] hover:from-[#1d4ed8] hover:to-[#6d28d9] text-white font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 text-sm sm:text-base whitespace-nowrap"
                >
                  <CalendarDays className="h-4 w-4" />
                  <span className="hidden sm:inline">Aujourd'hui</span>
                  <span className="sm:hidden">Auj.</span>
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Planning Content */}
      {activeTab === 'planning' && (
        <div className="space-y-6">

          {/* Vue Timeline Hebdomadaire - Par Technicien */}
          {viewMode === 'timeline' && (
            <Card className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
              <CardContent className="p-0">
                {/* Grille Timeline */}
                <div className="overflow-x-auto">
                  <div className="min-w-[900px]">
                    {/* En-tête avec dates */}
                    <div className="grid grid-cols-[150px_repeat(7,1fr)] sm:grid-cols-[200px_repeat(7,1fr)] bg-gray-50 border-b-2 border-gray-200">
                      {/* Colonne Techniciens */}
                      <div className="p-2 sm:p-4 font-bold text-sm sm:text-base text-gray-700 border-r-2 border-gray-200 bg-white">
                        Techniciens
                      </div>
                      {/* Colonnes des jours */}
                      {getWeekDates(currentDate).map((date, index) => {
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                        const dayName = new Intl.DateTimeFormat('fr-FR', { weekday: 'short' }).format(date);
                        const dayNum = date.getDate();
                        const month = new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(date);
                        
                        return (
                          <div 
                            key={index}
                            className={`p-2 sm:p-3 text-center border-r border-gray-200 ${
                              isToday 
                                ? 'bg-blue-50 border-blue-300' 
                                : isWeekend 
                                  ? 'bg-gray-100' 
                                  : 'bg-white'
                            }`}
                          >
                            <div className={`text-xs font-semibold uppercase ${
                              isToday ? 'text-blue-600' : 'text-gray-600'
                            }`}>
                              {dayName}
                            </div>
                            <div className={`text-xl sm:text-2xl font-bold mt-1 ${
                              isToday ? 'text-blue-700' : 'text-gray-800'
                            }`}>
                              {dayNum}
                            </div>
                            <div className="text-xs text-gray-500">
                              {month}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Indicateur drag & drop */}
                    {draggedSchedule && (
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 border-b-2 border-green-400 px-4 py-2 flex items-center gap-2 animate-pulse">
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-bold">↓</div>
                        <span className="text-sm font-semibold text-green-700">
                          Déposez sur un technicien pour assigner : <span className="text-green-900">{draggedSchedule.worksite_title || 'Mission'}</span>
                        </span>
                      </div>
                    )}

                    {/* Lignes par chantier — 1 ligne = 1 chantier avec segments journaliers */}
                    {(() => {
                      const weekDates = getWeekDates(currentDate);
                      const weekStart = new Date(weekDates[0]); weekStart.setHours(0,0,0,0);
                      const weekEnd = new Date(weekDates[6]); weekEnd.setHours(23,59,59,999);

                      // ── Grouper les schedules ASSIGNÉS par worksite_id ──
                      // On garde les missions clôturées/terminées (completed,
                      // partially_completed) affichées avec un badge ; on masque
                      // uniquement les annulées.
                      const FINAL_STATUSES = ['completed', 'cancelled', 'partially_completed'];

                      // Utiliser le memo (overrides déjà appliqués)
                      const effectiveSchedules = effectiveSchedulesData;

                      const assignedSchedules = effectiveSchedules.filter(s => s.collaborator_id && s.status !== 'unassigned' && s.status !== 'cancelled');


                      // Schedules sans collaborateur (unassigned)
                      const unassignedSchedules = effectiveSchedules.filter(s => (!s.collaborator_id || s.status === 'unassigned') && !FINAL_STATUSES.includes(s.status));

                      // Couleurs par chantier
                      const wsColors = [
                        { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-400', accent: 'bg-blue-500', light: 'bg-blue-50', hover: 'hover:bg-blue-200' },
                        { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-400', accent: 'bg-purple-500', light: 'bg-purple-50', hover: 'hover:bg-purple-200' },
                        { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-400', accent: 'bg-green-500', light: 'bg-green-50', hover: 'hover:bg-green-200' },
                        { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-400', accent: 'bg-orange-500', light: 'bg-orange-50', hover: 'hover:bg-orange-200' },
                        { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-400', accent: 'bg-pink-500', light: 'bg-pink-50', hover: 'hover:bg-pink-200' },
                        { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-400', accent: 'bg-teal-500', light: 'bg-teal-50', hover: 'hover:bg-teal-200' },
                        { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-400', accent: 'bg-indigo-500', light: 'bg-indigo-50', hover: 'hover:bg-indigo-200' },
                        { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-400', accent: 'bg-red-500', light: 'bg-red-50', hover: 'hover:bg-red-200' },
                      ];

                      // ── Créer les groupes d'équipes (utile pour la section "sans équipe" + drag) ──
                      const teamGroups = {};
                      const assignedTechIds = new Set();

                      // D'abord, créer tous les groupes d'équipes
                      teamLeaders.forEach(leader => {
                        teamGroups[leader.id] = { leader, members: [] };
                        // Le chef d'équipe est lui-même "assigné" : ne pas le lister en "sans équipe"
                        if (leader.user_id) {
                          const leaderTech = collaborators.find(c => c.id === leader.user_id || c.user_id === leader.user_id);
                          if (leaderTech) assignedTechIds.add(leaderTech.id);
                        }
                        const teamMembers = teamCollaborators[leader.id] || [];
                        teamMembers.forEach(memberData => {
                          const memberId = typeof memberData === 'object' ? (memberData.id || memberData.user_id || memberData.collaborator_id) : memberData;
                          const tech = collaborators.find(c => c.id === memberId || c.user_id === memberId);
                          if (tech && !teamGroups[leader.id].members.find(m => m.id === tech.id)) {
                            teamGroups[leader.id].members.push(tech);
                            assignedTechIds.add(tech.id);
                          }
                        });
                      });
                      const unassignedTechs = collaborators.filter(tech => !assignedTechIds.has(tech.id));



                      // ── Trier les équipes : actives d'abord ──
                      const sortedTeamEntries = Object.entries(teamGroups).sort((a, b) => {
                        const hasA = assignedSchedules.some(s => s.team_leader_id === a[0]);
                        const hasB = assignedSchedules.some(s => s.team_leader_id === b[0]);
                        if (hasA && !hasB) return -1;
                        if (!hasA && hasB) return 1;
                        const nameA = `${a[1].leader?.prenom || ''} ${a[1].leader?.nom || ''}`;
                        const nameB = `${b[1].leader?.prenom || ''} ${b[1].leader?.nom || ''}`;
                        return nameA.localeCompare(nameB);
                      });

                      // Helper : couleur par worksite_id (stable)
                      const wsColorMap = {};
                      let wsColorIdx = 0;
                      const getWsColor = (wsId) => {
                        if (!wsColorMap[wsId]) { wsColorMap[wsId] = wsColors[wsColorIdx % wsColors.length]; wsColorIdx++; }
                        return wsColorMap[wsId];
                      };

                      return (
                        <>
                          {/* ═══ SECTION ÉQUIPES — 1 ligne = 1 équipe, cartes chantier à droite ═══ */}
                          {sortedTeamEntries.map(([leaderId, teamData], teamIdx) => {
                            const teamColor = wsColors[teamIdx % wsColors.length];
                            const teamSchedules = assignedSchedules.filter(s => s.team_leader_id === leaderId);
                            const hasSchedules = teamSchedules.length > 0;

                            // Pré-calcul lanes pour gridTemplateRows
                            const _dedupBlocks = deduplicateTeamSchedules(teamSchedules, weekDates[0]);
                            const _lanes = [];
                            _dedupBlocks.forEach(block => {
                              const colEnd = block.columnStart + block.columnSpan;
                              for (let i = 0; i < _lanes.length; i++) {
                                if (_lanes[i].every(b => block.columnStart >= b.colEnd || colEnd <= b.colStart)) {
                                  _lanes[i].push({ colStart: block.columnStart, colEnd });
                                  return;
                                }
                              }
                              _lanes.push([{ colStart: block.columnStart, colEnd }]);
                            });
                            const _totalLanes = Math.max(_lanes.length, 1);

                            return (
                              <div 
                                key={leaderId}
                                className={`grid grid-cols-[150px_repeat(7,1fr)] sm:grid-cols-[200px_repeat(7,1fr)] border-b-2 ${hasSchedules ? 'border-gray-400' : 'border-gray-200'} ${draggedSchedule ? 'hover:ring-2 hover:ring-green-400 hover:ring-inset' : ''}`}
                                style={{ gridTemplateRows: `repeat(${_totalLanes}, auto)` }}
                                onDragOver={handleDragOver}
                                onDrop={(e) => {
                                  const firstMember = teamData.members[0];
                                  const collabId = firstMember ? (firstMember.user_id || firstMember.id) : null;
                                  if (collabId) handleDropOnCollaborator(e, collabId, leaderId);
                                }}
                              >
                                {/* ─ Colonne gauche — Équipe (chef + membres) ─ */}
                                <div className={`p-2 sm:p-3 border-r-2 border-gray-200 ${teamColor.bg} ${hasSchedules ? 'border-l-4 border-l-green-500' : ''}`}
                                  style={{ gridRow: `1 / span ${_totalLanes}` }}>
                                  {/* Badge équipe active */}
                                  {hasSchedules && (
                                    <div className="mb-1.5 text-center">
                                      <span className="text-[9px] font-bold bg-green-500 text-white px-2 py-0.5 rounded-full">✓ ACTIVE</span>
                                    </div>
                                  )}
                                  {/* Chef d'équipe */}
                                  {teamData.leader && (
                                    <div className="mb-2 pb-2 border-b border-gray-300">
                                      <div className="flex items-center space-x-2">
                                        <div className={`w-9 h-9 rounded-full ${teamColor.accent} flex items-center justify-center text-white font-bold text-xs shadow`}>
                                          👤
                                        </div>
                                        <div className="min-w-0">
                                          <div className={`font-bold ${teamColor.text} text-xs truncate`}>
                                            {teamData.leader.prenom} {teamData.leader.nom}
                                          </div>
                                          <div className="text-[10px] text-gray-600 font-semibold">👨‍💼 Chef d'équipe</div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {/* Membres */}
                                  <div className="space-y-1.5">
                                    {teamData.members.map((member) => (
                                      <div key={member.id} className="flex items-center space-x-1.5 pl-1">
                                        <div className={`w-6 h-6 rounded-full ${teamColor.bg} border-2 ${teamColor.border} flex items-center justify-center ${teamColor.text} font-semibold text-[9px]`}>
                                          {(member.first_name?.[0] || '') + (member.last_name?.[0] || '')}
                                        </div>
                                        <div className="font-medium text-gray-800 text-[10px] truncate">
                                          {member.first_name} {member.last_name}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="mt-2 pt-1.5 border-t border-gray-300 text-center">
                                    <span className="text-[10px] font-semibold text-gray-600">{teamData.members.length + 1} membre{teamData.members.length > 0 ? 's' : ''}</span>
                                  </div>
                                </div>

                                {/* ─ 7 colonnes jour — Cartes chantier (multi-jours = bloc continu) ─ */}
                                {(() => {
                                  // Calculer les blocs dédupliqués avec span pour cette équipe
                                  const dedupBlocks = deduplicateTeamSchedules(teamSchedules, weekDates[0]);

                                  // Algorithme de lanes : empiler uniquement les blocs qui se chevauchent
                                  const lanes = []; // chaque lane = [{ colEnd }]
                                  const blockLanes = dedupBlocks.map(block => {
                                    const colEnd = block.columnStart + block.columnSpan;
                                    for (let i = 0; i < lanes.length; i++) {
                                      if (lanes[i].every(b => block.columnStart >= b.colEnd || colEnd <= b.colStart)) {
                                        lanes[i].push({ colStart: block.columnStart, colEnd });
                                        return i;
                                      }
                                    }
                                    lanes.push([{ colStart: block.columnStart, colEnd }]);
                                    return lanes.length - 1;
                                  });
                                  const blockHeight = 76;
                                  const totalLanes = Math.max(lanes.length, 1);

                                  return (
                                    <>
                                      {weekDates.map((date, dayIndex) => {
                                        const isToday = date.toDateString() === new Date().toDateString();
                                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                                        const hasCoverage = dedupBlocks.some(b => dayIndex + 1 >= b.columnStart && dayIndex + 1 < b.columnStart + b.columnSpan);
                                        return (
                                          <div
                                            key={dayIndex}
                                            className={`relative border-r border-gray-100 transition-colors ${
                                              isToday ? 'bg-blue-50/40' : isWeekend ? 'bg-gray-50/50' : 'bg-white'
                                            }`}
                                            style={{ gridColumn: dayIndex + 2, gridRow: `1 / span ${totalLanes}` }}
                                          >
                                            {!hasCoverage && (
                                              <div className="h-full flex items-center justify-center">
                                                <div className={`w-8 h-0.5 ${isWeekend ? 'bg-gray-200' : 'bg-gray-150'} rounded`}></div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {/* Blocs chantier rendus en span continu */}
                                      {dedupBlocks.map((block, blockIdx) => {
                                        const color = getWsColor(block.worksite_id || 'no-ws');
                                        const isPaused = block.status === 'paused';
                                        const isCompleted = block.status === 'completed' || block.status === 'partially_completed';
                                        const totalH = parseInt(block.hours) || 0;
                                        const startDateStr = block.period_start || block.start_date || block.date;
                                        const endDateStr = block.period_end || block.end_date || startDateStr;
                                        const lane = blockLanes[blockIdx];

                                        return (
                                          <div
                                            key={`${block.worksite_id}-${block._segKey || block.id || blockIdx}`}
                                            className={`rounded-xl border cursor-pointer transition-all duration-200 group/seg overflow-hidden shadow-[0_4px_12px_rgba(15,23,42,0.07)] hover:shadow-[0_10px_28px_rgba(15,23,42,0.12)] hover:-translate-y-0.5 ${
                                              isCompleted
                                                ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-100/60 hover:from-green-100 hover:to-emerald-200/60'
                                                : isPaused
                                                ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-amber-100/60 hover:from-amber-100 hover:to-amber-200/60'
                                                : block._postponed_placeholder
                                                  ? 'border-gray-300 border-dashed bg-gradient-to-br from-gray-50 to-gray-100/80 hover:from-gray-100 hover:to-gray-200/80 opacity-80'
                                                  : block._override_id
                                                    ? 'border-gray-300 bg-gradient-to-br from-white to-gray-50 hover:from-gray-50 hover:to-gray-100'
                                                    : `${color.border} bg-gradient-to-br from-white ${color.light} hover:${color.bg}`
                                            }`}
                                            style={{
                                              gridColumn: `${block.columnStart + 1} / span ${block.columnSpan}`,
                                              gridRow: lane + 1,
                                              marginLeft: '4px',
                                              marginRight: '4px',
                                              marginTop: '3px',
                                              marginBottom: '3px',
                                              zIndex: 2,
                                              position: 'relative',
                                            }}
                                            onClick={() => {
                                              setSelectedSchedule({
                                                ...block,
                                                _daySiblings: [block],
                                                teamLeader: teamData.leader,
                                              });
                                              setShowScheduleDetail(true);
                                            }}
                                          >
                                            {/* Barre colorée */}
                                            <div className={`h-1.5 ${isCompleted ? 'bg-green-500' : isPaused ? 'bg-amber-400' : block._postponed_placeholder ? 'bg-gray-300' : block._override_id ? 'bg-gradient-to-r from-gray-400 to-gray-500' : color.accent}`}></div>
                                            {/* Placeholder grisé "Journée déplacée" */}
                                            {block._postponed_placeholder ? (
                                              <div className="p-2 overflow-hidden min-w-0 flex flex-col items-center justify-center text-center h-full">
                                                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-base mb-1">🔁</div>
                                                <div className="text-[9px] font-bold text-gray-600 uppercase tracking-wide leading-tight">Journée déplacée</div>
                                                <div className="text-[9px] text-gray-500 truncate w-full mt-1">{block.worksite_title || 'Mission'}</div>
                                                {(() => {
                                                  const ov = overrides.find(o => o.id === block._override_id);
                                                  return ov?.new_date ? (
                                                    <div className="text-[9px] text-gray-600 font-semibold mt-0.5 px-1.5 py-0.5 bg-white rounded-md border border-gray-200">→ {new Date(ov.new_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</div>
                                                  ) : (
                                                    <div className="text-[9px] text-amber-700 font-semibold mt-0.5 px-1.5 py-0.5 bg-amber-50 rounded-md border border-amber-200">À programmer</div>
                                                  );
                                                })()}
                                              </div>
                                            ) : (
                                            <>
                                            {/* Badge "Mission clôturée" */}
                                            {isCompleted && (
                                              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1.5 border-b border-green-400 shadow-sm">
                                                <span className="w-4 h-4 rounded-full bg-white/25 flex items-center justify-center text-[10px]">✓</span>
                                                <span>{block.status === 'partially_completed' ? 'Partiellement clôturée' : 'Mission clôturée'}</span>
                                              </div>
                                            )}
                                            {/* Badge "Journée déplacée" */}
                                            {block._override_id && (
                                              <div className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1.5 border-b border-gray-200">
                                                <span>🔁</span>
                                                <span>Journée déplacée</span>
                                              </div>
                                            )}
                                            <div className="p-3 overflow-hidden min-w-0 space-y-1.5">
                                              {/* Titre chantier */}
                                              <div className={`text-[13px] font-bold ${isCompleted ? 'text-green-900' : isPaused ? 'text-amber-900' : block._override_id ? 'text-gray-900' : color.text} leading-snug flex items-start gap-1.5`}>
                                                <span className="text-[13px] flex-shrink-0">🏗️</span>
                                                <span className="truncate">{block.worksite_title || block.worksites?.title || 'Mission'}</span>
                                              </div>
                                              {/* Badge Urgence */}
                                              {block.intervention_category === 'urgence' && (
                                                <div className="inline-flex items-center gap-1 bg-red-100 text-red-700 rounded-md px-2 py-0.5 text-[10.5px] font-bold w-fit">
                                                  🚨 Urgence
                                                </div>
                                              )}
                                              {/* Client */}
                                              {(block.client_name || block.worksites?.clients?.name) && (
                                                <div className="text-[11.5px] text-gray-600 truncate flex items-center gap-1.5 pl-0.5">
                                                  <span className="text-[11px] opacity-70">👤</span>
                                                  <span className="truncate font-medium">{block.client_name || block.worksites?.clients?.name}</span>
                                                </div>
                                              )}
                                              {/* Adresse chantier */}
                                              {(block.client_address || block.worksites?.address) && (
                                                <div className="text-[11px] text-gray-500 truncate flex items-center gap-1.5 pl-0.5">
                                                  <span className="text-[11px] opacity-70">📍</span>
                                                  <span className="truncate">{block.client_address || block.worksites?.address}</span>
                                                </div>
                                              )}
                                              {/* Date d'origine si déplacée */}
                                              {block._override_id && (
                                                <div className="text-[10.5px] text-gray-500 italic truncate flex items-center gap-1">
                                                  <span>↶</span>
                                                  <span>Initialement le {new Date(overrides.find(o => o.id === block._override_id)?.original_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                                                </div>
                                              )}
                                              {/* Durée + dates */}
                                              <div className="flex items-center justify-between gap-2 pt-1.5 mt-1.5 border-t border-gray-200/70">
                                                <span className="text-[11px] font-semibold text-gray-700 flex items-center gap-1.5 min-w-0">
                                                  {block.isMultiDay ? (
                                                    <>
                                                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${isCompleted ? 'bg-green-600 text-white' : isPaused ? 'bg-amber-500 text-white' : `${color.accent} text-white`}`}>{block.dayCount}j</span>
                                                      <span className="truncate">
                                                        {new Date(startDateStr + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                                        {' → '}
                                                        {new Date(endDateStr + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                                      </span>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <span className="text-[12px]">🕐</span>
                                                      <span>{block.time || '08:00'}</span>
                                                    </>
                                                  )}
                                                </span>
                                                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold flex-shrink-0 ${isCompleted ? 'bg-green-100 text-green-700' : isPaused ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                                                  {isCompleted && <span>✓</span>}
                                                  {isPaused && <span>⏸</span>}
                                                  <span>{totalH}h</span>
                                                </span>
                                              </div>
                                              {block.night_surcharge_applied && (
                                                <div className="flex items-center gap-1 bg-indigo-100 text-indigo-700 rounded-md px-2 py-0.5 text-[10.5px] font-bold">
                                                  🌙 {block.night_surcharge_label || 'Majoration nuit'} : {block.night_surcharge_type === 'fixed' ? `+${parseFloat(block.night_surcharge_value).toFixed(2)}€` : `+${block.night_surcharge_value}%`}
                                                </div>
                                              )}
                                            </div>
                                            </>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                              </div>
                            );
                          })}

                          {/* ═══ COLLABORATEURS SANS ÉQUIPE ═══ */}
                          {unassignedTechs.length > 0 && (
                            <>
                              <div className="bg-gray-100 p-2 border-y-2 border-gray-400">
                                <div className="text-sm font-bold text-gray-600 text-center">
                                  👤 COLLABORATEURS SANS ÉQUIPE
                                </div>
                              </div>
                              {unassignedTechs.map((tech, techIndex) => {
                                const tColor = wsColors[(sortedTeamEntries.length + techIndex) % wsColors.length];
                                return (
                                  <div 
                                    key={tech.id}
                                    className={`grid grid-cols-[150px_repeat(7,1fr)] sm:grid-cols-[200px_repeat(7,1fr)] border-b border-gray-200 transition-colors ${draggedSchedule ? 'hover:bg-green-50 hover:ring-2 hover:ring-green-400 hover:ring-inset' : 'hover:bg-gray-50'}`}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDropOnCollaborator(e, tech.user_id || tech.id, null)}
                                  >
                                    <div className="p-2 sm:p-3 border-r-2 border-gray-200 bg-gray-50">
                                      <div className="flex items-center space-x-2">
                                        <div className={`w-8 h-8 rounded-full ${tColor.bg} flex items-center justify-center ${tColor.text} font-bold text-xs`}>
                                          {(tech.first_name?.[0] || '') + (tech.last_name?.[0] || '')}
                                        </div>
                                        <div>
                                          <div className="font-semibold text-gray-800 text-xs">{tech.first_name} {tech.last_name}</div>
                                          <div className="text-[10px] text-gray-500">Sans équipe</div>
                                        </div>
                                      </div>
                                    </div>
                                    {weekDates.map((date, dayIndex) => {
                                      const dateStr = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
                                      const techSchedules = schedules.filter(s => {
                                        const sStart = s.period_start || s.start_date || s.date;
                                        const sEnd = s.period_end || s.end_date || sStart;
                                        return sStart && dateStr >= sStart && dateStr <= sEnd && s.collaborator_id === (tech.user_id || tech.id);
                                      });
                                      return (
                                        <div key={dayIndex} className="p-1 border-r border-gray-100 min-h-[60px] flex items-center justify-center overflow-hidden min-w-0">
                                          {techSchedules.length > 0 ? (
                                            <div className="w-full space-y-1">
                                              {techSchedules.map((s, idx) => {
                                                const color = getWsColor(s.worksite_id || 'no-ws');
                                                return (
                                                  <div key={s.id || `tech-sched-${idx}`} className={`rounded-lg border ${color.border} ${color.light} p-1 cursor-pointer ${color.hover} overflow-hidden`}
                                                    onClick={() => { setSelectedSchedule(s); setShowScheduleDetail(true); }}>
                                                    <div className={`text-[9px] font-bold ${color.text} truncate overflow-hidden text-ellipsis whitespace-nowrap`}>🏗️ {s.worksite_title || 'Mission'}</div>
                                                    <div className="text-[8px] text-gray-500">{s.time || '08:00'} · {s.hours || 8}h</div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <div className="w-6 h-0.5 bg-gray-200 rounded"></div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </>
                          )}

                          {/* ═══ MISSIONS NON ASSIGNÉES — Groupées par chantier ═══ */}
                          {unassignedSchedules.length > 0 && (() => {
                            const groupedByWorksite = {};
                            unassignedSchedules.forEach(s => {
                              const wsId = s.worksite_id || 'unknown';
                              if (!groupedByWorksite[wsId]) {
                                groupedByWorksite[wsId] = {
                                  worksite_id: wsId,
                                  worksite_title: s.worksite_title || s.worksites?.title || 'Sans chantier',
                                  client_name: s.client_name || s.worksites?.clients?.name || '',
                                  schedules: []
                                };
                              }
                              groupedByWorksite[wsId].schedules.push(s);
                            });
                            const worksiteGroups = Object.values(groupedByWorksite);

                            return (
                              <>
                                <div className="bg-orange-100 p-2 border-y-2 border-orange-400">
                                  <div className="text-sm font-bold text-orange-700 text-center flex items-center justify-center gap-2">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-500 text-white text-xs font-bold">{unassignedSchedules.length}</span>
                                    ⚠️ MISSIONS NON ASSIGNÉES
                                    <span className="text-xs font-normal text-orange-600">({worksiteGroups.length} chantier{worksiteGroups.length > 1 ? 's' : ''})</span>
                                  </div>
                                </div>

                                {worksiteGroups.map((group) => (
                                  <div key={group.worksite_id} className="grid grid-cols-[150px_repeat(7,1fr)] sm:grid-cols-[200px_repeat(7,1fr)] border-b border-orange-200 bg-orange-50/30">
                                    <div className="p-2 sm:p-3 border-r-2 border-orange-200 bg-orange-50">
                                      <div className="flex flex-col gap-2">
                                        <div>
                                          <div className="font-semibold text-orange-800 text-xs truncate">🏗️ {group.worksite_title}</div>
                                          {group.client_name && <div className="text-[10px] text-orange-600 truncate">👤 {group.client_name}</div>}
                                          <div className="text-[10px] text-orange-500">{group.schedules.length} mission{group.schedules.length > 1 ? 's' : ''}</div>
                                        </div>
                                        <button
                                          onClick={() => openAssignModal({ worksite_id: group.worksite_id, worksite_title: group.worksite_title, count: group.schedules.length }, 'batch')}
                                          className="w-full text-[10px] font-bold text-white bg-orange-500 hover:bg-orange-600 px-2 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1"
                                        >
                                          <UserPlus className="w-3 h-3" />
                                          Assigner tout
                                        </button>
                                      </div>
                                    </div>

                                    {weekDates.map((date, dayIndex) => {
                                      const dateStr = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
                                      const dayUnassigned = group.schedules.filter(s => {
                                        const sStart = s.period_start || s.start_date || s.date;
                                        const sEnd = s.period_end || s.end_date || sStart;
                                        return sStart && dateStr >= sStart && dateStr <= sEnd;
                                      });
                                      return (
                                        <div key={dayIndex} className="p-1.5 border-r border-orange-100 min-h-[80px] overflow-hidden min-w-0">
                                          {dayUnassigned.length > 0 ? (
                                            <div className="space-y-1.5">
                                              {dayUnassigned.map((schedule, idx) => (
                                                <div
                                                  key={schedule._segKey || schedule.id || `unassigned-${idx}`}
                                                  draggable
                                                  onDragStart={(e) => handleDragStart(e, schedule)}
                                                  onDragEnd={() => setDraggedSchedule(null)}
                                                  className="p-1.5 rounded-xl border-2 bg-orange-50 border-orange-400 text-orange-800 hover:bg-orange-100 transition-all duration-200 cursor-grab active:cursor-grabbing active:opacity-60 active:scale-105 active:shadow-xl shadow-sm relative group/card"
                                                >
                                                  <div className="flex items-center justify-between">
                                                    <span className="font-bold text-[10px]">{schedule.time || '08:00'}</span>
                                                    <span className="px-1 py-0.5 rounded bg-orange-500 text-white text-[7px] font-bold">⚠️</span>
                                                  </div>
                                                  <button
                                                    onClick={(e) => { e.stopPropagation(); openAssignModal(schedule, 'single'); }}
                                                    className="mt-1 w-full text-[8px] font-bold text-white bg-blue-500 hover:bg-blue-600 px-1 py-0.5 rounded transition-colors opacity-0 group-hover/card:opacity-100 flex items-center justify-center gap-0.5"
                                                  >
                                                    <UserPlus className="w-2.5 h-2.5" />
                                                    Assigner
                                                  </button>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="h-full flex items-center justify-center">
                                              <div className="w-6 h-0.5 bg-gray-200 rounded"></div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </>
                            );
                          })()}

                          {/* Message si rien */}
                          {sortedTeamEntries.length === 0 && unassignedSchedules.length === 0 && (
                            <div className="p-8 text-center text-gray-400">
                              <div className="text-4xl mb-2">📋</div>
                              <div className="font-semibold">Aucun planning cette semaine</div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* Message si aucun technicien */}
                    {collaborators.length === 0 && teamLeaders.length === 0 && (
                      <div className="p-12 text-center text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-lg font-semibold">Aucun technicien disponible</p>
                        <p className="text-sm mt-1">Ajoutez des collaborateurs pour commencer la planification</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vue Calendaire */}
          {viewMode === 'week' && (
            <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="grid grid-cols-7 gap-2">
                  {/* En-têtes des jours avec style amélioré */}
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, index) => {
                    const isWeekend = index >= 5;
                    return (
                      <div 
                        key={day} 
                        className={`p-3 text-center font-bold text-sm uppercase tracking-wider border-b-2 ${
                          isWeekend ? 'bg-gray-100 text-gray-600 border-gray-300' : 'bg-white text-gray-700 border-gray-200'
                        }`}
                      >
                        {day}
                      </div>
                    );
                  })}
                  
                  {/* Jours de la semaine avec style amélioré */}
                  {getWeekDates(currentDate).map((date, index) => {
                    const daySchedules = getSchedulesForDate(date);
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    
                    return (
                      <div 
                        key={index} 
                        className={`relative p-3 min-h-[120px] border-2 rounded-xl transition-all duration-200 ${
                          isToday 
                            ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-[#2563EB] shadow-md ring-2 ring-blue-200' 
                            : isWeekend
                              ? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              : isCurrentMonth 
                                ? 'bg-white border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:shadow-sm' 
                                : 'bg-gray-50 border-gray-100 text-gray-400'
                        } cursor-pointer`}
                        onClick={() => openDayDetail(date)}
                      >
                        {/* Badge aujourd'hui */}
                        {isToday && (
                          <div className="absolute top-1 right-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#2563EB] text-white">
                              Aujourd'hui
                            </span>
                          </div>
                        )}
                        
                        {/* Numéro du jour */}
                        <div className={`text-center mb-2 ${
                          isToday 
                            ? 'text-2xl font-bold text-[#1d4ed8]' 
                            : isCurrentMonth 
                              ? 'text-lg font-semibold text-gray-700' 
                              : 'text-base text-gray-400'
                        }`}>
                          {date.getDate()}
                        </div>
                        
                        {/* Indicateurs de planning */}
                        <div className="space-y-1.5">
                          {daySchedules.slice(0, 2).map((schedule, idx) => (
                            <div 
                              key={idx} 
                              className="group relative text-xs bg-blue-50 text-blue-800 px-2 py-1.5 rounded-lg truncate font-medium shadow-sm hover:shadow-md transition-all border border-blue-200"
                            >
                              <div className="flex items-center space-x-1">
                                <span className="inline-block w-1.5 h-1.5 bg-[#2563EB] rounded-full"></span>
                                <span>{schedule.time}</span>
                              </div>
                            </div>
                          ))}
                          {daySchedules.length > 2 && (
                            <div className="text-xs text-center font-semibold text-[#2563EB] bg-blue-50 py-1 rounded-lg border border-blue-200">
                              +{daySchedules.length - 2} autre{daySchedules.length - 2 > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                        
                        {/* Mini-indicateurs colorés si événements */}
                        {daySchedules.length > 0 && (
                          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                            {daySchedules.slice(0, 3).map((_, idx) => (
                              <div key={idx} className="w-1.5 h-1.5 bg-[#2563EB] rounded-full"></div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {viewMode === 'month' && (
            <section aria-label="Calendrier mensuel" className="overflow-x-auto border border-gray-200 bg-white">
              <div className="min-w-[700px]">
                <div className="grid grid-cols-7 gap-px bg-gray-200">
                  {/* En-têtes des jours avec style amélioré */}
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, index) => {
                    const isWeekend = index >= 5;
                    return (
                      <div 
                        key={day} 
                        className={`p-3 text-center font-semibold text-xs uppercase border-b ${
                          isWeekend ? 'bg-gray-100 text-gray-600 border-gray-300' : 'bg-white text-gray-700 border-gray-200'
                        }`}
                      >
                        {day}
                      </div>
                    );
                  })}
                  
                  {/* Jours du mois avec améliorations visuelles */}
                  {getMonthDates(currentDate).map((date, index) => {
                    const daySchedules = deduplicateTeamSchedules(getSchedulesForDate(date), getWeekDates(date)[0]);
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const dayOfWeek = date.getDay();
                    
                    return (
                      <div 
                        key={index} 
                        className={`relative p-2 h-[178px] min-w-0 overflow-hidden ${
                          isToday 
                            ? 'bg-blue-50 ring-1 ring-inset ring-blue-500' 
                            : isWeekend
                              ? 'bg-gray-50 hover:bg-gray-100'
                              : isCurrentMonth 
                                ? 'bg-white hover:bg-blue-50' 
                                : 'bg-gray-50 text-gray-400'
                        } cursor-pointer`}
                      >
                        {/* Badge aujourd'hui */}
                        
                        {/* Numéro du jour */}
                        <button type="button" onClick={() => openDayDetail(date)} aria-label={date.toLocaleDateString('fr-FR', { dateStyle: 'full' })} aria-current={isToday ? 'date' : undefined} className={`flex items-center justify-between w-full h-7 mb-1 text-left ${
                          isToday 
                            ? 'text-sm font-bold text-[#1d4ed8]' 
                            : isCurrentMonth 
                              ? 'text-sm font-semibold text-gray-700' 
                              : 'text-sm text-gray-400'
                        }`}>
                          {date.getDate()}
                          <span className="text-xs font-normal">{daySchedules.length || ''}</span>
                        </button>
                        
                        {/* Indicateurs de planning */}
                        <div className="space-y-1.5">
                          {daySchedules.slice(0, 2).map((schedule) => (
                            <button type="button"
                              key={schedule._segKey || schedule.id}
                              onClick={() => { setSelectedSchedule(schedule); setShowScheduleDetail(true); }}
                              title={`${schedule.worksites?.title || schedule.worksite_title || schedule.description || 'Intervention'} · ${getTeamLeaderName(schedule.team_leader_id)}`}
                              className={`block w-full min-w-0 text-left text-xs px-2 py-1 rounded border-l-4 ${schedule.status === 'paused' ? 'bg-amber-50 text-amber-900 border-amber-500' : schedule.status === 'completed' ? 'bg-green-50 text-green-800 border-green-600' : schedule._isVirtualPostpone ? 'bg-teal-50 text-teal-900 border-teal-500' : 'bg-blue-50 text-blue-900 border-blue-500'}`}
                            >
                              <span className="block truncate font-semibold">{(schedule.time || '').slice(0, 5)} · {schedule.worksites?.title || schedule.worksite_title || schedule.description || 'Intervention'}</span>
                              <span className="block truncate">{getTeamLeaderName(schedule.team_leader_id)} · {schedule._ids?.length || 1} pers.</span>
                              <span className="block truncate">{schedule._isVirtualPostpone ? 'Reportée' : ({ paused: 'En pause', completed: 'Terminée', partially_completed: 'Partiellement terminée', cancelled: 'Annulée', unassigned: 'Non affectée', in_progress: 'En cours', scheduled: 'Planifiée' })[getEffectiveStatus(schedule)] || schedule.status}</span>
                            </button>
                          ))}
                          {daySchedules.length > 2 && (
                            <button type="button" onClick={() => openDayDetail(date)} className="w-full text-xs text-left font-semibold text-[#2563EB] py-1">
                              +{daySchedules.length - 2} autre{daySchedules.length - 2 > 1 ? 's' : ''}
                            </button>
                          )}
                        </div>
                        
                        {/* Mini-indicateurs colorés si événements */}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* Date Selector pour le détail */}
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Détail du jour sélectionné</h3>
                  <p className="text-sm text-gray-600">Planning détaillé pour la date sélectionnée</p>
                </div>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3"
                />
              </div>
            </CardContent>
          </Card>

          {/* Schedules for Selected Date - Vue cartes compactes */}
          <div className="grid gap-3">
            {getSchedulesForDate(selectedDate).length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun planning</h3>
                  <p className="text-gray-500">Aucune intervention programmée pour cette date.</p>
                </CardContent>
              </Card>
            ) : (
              getSchedulesForDate(selectedDate).map((schedule) => {
                const typeColors = {
                  rdv: { bg: 'bg-blue-500', text: 'text-blue-500', light: 'bg-blue-50' },
                  worksite: { bg: 'bg-orange-500', text: 'text-orange-500', light: 'bg-orange-50' },
                  urgence: { bg: 'bg-red-500', text: 'text-red-500', light: 'bg-red-50' }
                };
                const colors = typeColors[schedule.intervention_category] || typeColors.rdv;
                
                return (
                  <div 
                    key={schedule.id}
                    className="group bg-white rounded-[18px] border border-[rgba(15,23,42,0.08)] hover:border-blue-200 transition-all duration-200 overflow-hidden hover:shadow-[0_10px_30px_rgba(37,99,235,0.09)] hover:-translate-y-px"
                  >
                    <div className="flex">
                      {/* Bande latérale avec heure */}
                      <div className={`${colors.bg} w-24 flex flex-col items-center justify-center text-white p-4`}>
                        <div className="text-3xl font-bold leading-none">{schedule.time.split(':')[0]}</div>
                        <div className="text-sm opacity-90">:{schedule.time.split(':')[1]}</div>
                        <div className="text-xs opacity-75 mt-2">{schedule.hours}h</div>
                      </div>
                      
                      {/* Contenu principal */}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Type + Titre */}
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 ${colors.light} ${colors.text} text-xs font-bold rounded`}>
                                {schedule.intervention_category === 'rdv' ? 'RDV' :
                                 schedule.intervention_category === 'worksite' ? 'CHANTIER' :
                                 'URGENCE'}
                              </span>
                              {schedule.intervention_category === 'worksite' && (
                                <span className="text-sm text-gray-600">
                                  {getWorksiteName(schedule.worksite_id)}
                                </span>
                              )}
                            </div>
                            
                            {/* Nom principal en GROS */}
                            <div className="mb-2">
                              {schedule.intervention_category === 'worksite' ? (
                                // Pour chantier : nom du chef en GROS
                                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                  <span className="text-xl">👨‍💼</span>
                                  {schedule.team_leader_id ? getTeamLeaderName(schedule.team_leader_id) : 'Chef non assigné'}
                                </h3>
                              ) : (
                                // Pour RDV/Urgence : nom de la personne qui a le RDV en GROS
                                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                  {schedule.team_leader_id ? (
                                    <>
                                      <span className="text-xl">👨‍💼</span>
                                      {getTeamLeaderName(schedule.team_leader_id)}
                                    </>
                                  ) : schedule.collaborator_id ? (
                                    <>
                                      <span className="text-xl">👷</span>
                                      {getCollaboratorName(schedule.collaborator_id)}
                                    </>
                                  ) : (
                                    <span className="text-gray-400">Personne non assignée</span>
                                  )}
                                </h3>
                              )}
                            </div>
                            
                            {/* Informations du RDV/Urgence */}
                            {schedule.intervention_category !== 'worksite' && (
                              <div className="mb-2 space-y-1">
                                {schedule.client_name && (
                                  <div className="text-base font-semibold text-gray-800">
                                    Client: {schedule.client_name}
                                  </div>
                                )}
                                {schedule.client_address && (
                                  <div className="text-sm text-gray-600 flex items-start gap-1.5">
                                    <span className="mt-0.5">📍</span>
                                    <span>{schedule.client_address}</span>
                                  </div>
                                )}
                                {schedule.client_contact && (
                                  <div className="text-sm text-gray-600 flex items-center gap-1.5">
                                    <span>📞</span>
                                    <span>{schedule.client_contact}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Détails en ligne */}
                            <div className="flex flex-wrap items-center gap-4 text-sm mb-2">
                              {schedule.intervention_category === 'worksite' && schedule.collaborator_id && (
                                <div className="flex items-center gap-1.5 text-gray-700">
                                  <span className="font-medium">Technicien:</span>
                                  <span className="text-base font-semibold">{getCollaboratorName(schedule.collaborator_id)}</span>
                                </div>
                              )}
                              {schedule.intervention_category !== 'worksite' && schedule.team_leader_id && schedule.collaborator_id && (
                                <div className="flex items-center gap-1.5 text-gray-700">
                                  <span className="font-medium">+</span>
                                  <span className="text-sm font-semibold">{getCollaboratorName(schedule.collaborator_id)}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 text-gray-500">
                                <Clock className="h-3.5 w-3.5" />
                                <span>{getShiftLabel(schedule.shift)}</span>
                              </div>
                              {schedule.night_surcharge_applied && (
                                <div className="flex items-center gap-1 bg-indigo-100 text-indigo-700 rounded-lg px-2 py-0.5 text-xs font-semibold">
                                  🌙 {schedule.night_surcharge_label || 'Majoration nuit'} : {schedule.night_surcharge_type === 'fixed' ? `+${parseFloat(schedule.night_surcharge_value).toFixed(2)}€` : `+${schedule.night_surcharge_value}%`}
                                </div>
                              )}
                            </div>
                            
                            {/* Description */}
                            {schedule.description && (
                              <div className="mt-2 text-sm text-gray-600 italic">
                                {schedule.description}
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-3">
                            <button className="p-2 hover:bg-gray-100 rounded-lg">
                              <Edit className="h-4 w-4 text-gray-600" />
                            </button>
                            <button 
                              onClick={() => deleteSchedule(schedule.id)}
                              className="p-2 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Statut vertical */}
                      <div className={`w-1 ${
                        schedule.status === 'scheduled' ? 'bg-blue-400' :
                        schedule.status === 'in_progress' ? 'bg-orange-400' :
                        schedule.status === 'paused' ? 'bg-amber-400' :
                        'bg-green-400'
                      }`}></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Team Leaders Tab - Drag & Drop Simple */}
      {activeTab === 'teams' && (
        <TeamManagementDragDrop activeTab={activeTab} />
      )}

      {/* Team Leaders Tab ANCIEN (désactivé) */}
      {activeTab === 'teams_disabled_old' && (
        <div className="grid gap-4">
          {teamLeaders.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <UserPlus className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun chef d'équipe</h3>
                <p className="text-gray-500">Ajoutez votre premier chef d'équipe pour commencer.</p>
              </CardContent>
            </Card>
          ) : (
            teamLeaders.map((leader) => (
              <Card key={leader.id} className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: leader.couleur }}
                      >
                        {leader.prenom.charAt(0)}{leader.nom.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{leader.prenom} {leader.nom}</h3>
                        <p className="text-gray-600">{leader.specialite}</p>
                        <div className="mt-2 space-y-1 text-sm text-gray-500">
                          <p>📧 {leader.email}</p>
                          <p>📱 {leader.telephone}</p>
                          <p>🏷️ Couleur: <span className="inline-block w-4 h-4 rounded ml-1" style={{ backgroundColor: leader.couleur }}></span></p>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">Ajouté le {new Date(leader.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" className="rounded-xl">
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => removeTeamLeader(leader.id)}
                        className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Collaborators Tab */}
      {activeTab === 'collaborators' && (
        <div className="grid gap-4">
          {collaborators.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun collaborateur</h3>
                <p className="text-gray-500">Ajoutez votre premier collaborateur pour commencer.</p>
              </CardContent>
            </Card>
          ) : (
            collaborators.map((collaborator) => {
              const teamLeader = teamLeaderMap.get(collaborator.team_leader_id);
              return (
                <Card key={collaborator.id} className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex items-start space-x-3 sm:space-x-4 min-w-0 flex-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-xl flex-shrink-0 flex items-center justify-center">
                          <Users className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="text-base sm:text-xl font-bold text-gray-900 truncate">{collaborator.first_name} {collaborator.last_name}</h3>
                            {collaborator.is_invited && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                ✓ Invité
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">🔧 {collaborator.role || 'TECHNICIEN'}</p>
                          <div className="space-y-1 text-xs sm:text-sm text-gray-600">
                            {collaborator.email && !collaborator.email.includes('@temp-skyapp.local') && (
                              <p className="truncate">📧 {collaborator.email}</p>
                            )}
                            <p>📱 {collaborator.phone || '-'}</p>
                          </div>
                          <p className="text-xs text-gray-400 mt-2 sm:mt-3">Ajouté le {new Date(collaborator.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2 sm:flex-shrink-0 ml-13 sm:ml-0">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="rounded-xl text-xs sm:text-sm"
                          onClick={() => openEditCollaboratorModal(collaborator)}
                        >
                          <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                          Modifier
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => removeCollaborator(collaborator.id)}
                          className="rounded-xl border-red-200 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                        >
                          <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="grid gap-4">
          {acceptedInvites.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <UserPlus className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune personne invitée</h3>
                <p className="text-gray-500">Les personnes qui ont accepté vos invitations apparaîtront ici et pourront être assignées aux équipes.</p>
              </CardContent>
            </Card>
          ) : (
            acceptedInvites.map((invitation) => (
              <Card key={invitation.id} className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-start space-x-3 sm:space-x-4 min-w-0 flex-1">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-100 to-blue-100 rounded-xl flex-shrink-0 flex items-center justify-center">
                        <UserPlus className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                          {invitation.firstName} {invitation.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">Rôle: {invitation.role}</p>
                        <div className="mt-2 space-y-1 text-xs sm:text-sm text-gray-500">
                          <p className="truncate">📧 {invitation.email}</p>
                          {invitation.phone && <p>📱 {invitation.phone}</p>}
                          <p>Compétences: {invitation.skills || "Généraliste"}</p>
                          <p>✅ Accepté le: {new Date(invitation.accepted_at || invitation.createdAt).toLocaleDateString('fr-FR')}</p>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5 sm:space-x-2">
                          {invitation.usedAsTeamLeader && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                              👑 Chef assigné
                            </Badge>
                          )}
                          {invitation.usedAsCollaborator && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              🤝 Collab. assigné
                            </Badge>
                          )}
                          {!invitation.usedAsTeamLeader && !invitation.usedAsCollaborator && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              ✅ Disponible
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex sm:flex-col space-x-2 sm:space-x-0 sm:space-y-2 ml-13 sm:ml-0">
                      {!invitation.usedAsTeamLeader && (
                        <Button 
                          size="sm"
                          onClick={() => addInvitedPersonAsTeamLeader(invitation)}
                          className="bg-gray-800 hover:bg-black text-white rounded-xl text-xs px-3 py-2"
                        >
                          👑 Chef
                        </Button>
                      )}
                      {!invitation.usedAsCollaborator && (
                        <Button 
                          size="sm"
                          onClick={() => addInvitedPersonAsCollaborator(invitation)}
                          className="bg-gray-600 hover:bg-gray-700 text-white rounded-xl text-xs px-3 py-2"
                        >
                          🤝 Collab.
                        </Button>
                      )}
                      {(invitation.usedAsTeamLeader || invitation.usedAsCollaborator) && (
                        <div className="text-center">
                          <p className="text-xs text-green-600 font-medium">✅ Assigné</p>
                          <p className="text-xs text-gray-500">Déjà dans l'équipe</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Interventions Tab */}
      {activeTab === 'interventions' && (
        <Card className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-0 shadow-lg">
          <CardContent className="p-4 sm:p-6">
            {/* Sous-onglets */}
            <div className="flex space-x-1 bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto">
              {[
                { key: 'completed', label: 'Terminées', icon: '✓', count: schedules.filter(s => s.status === 'completed' || s.status === 'partially_completed').length },
                { key: 'paused', label: 'En Pause', icon: '⏸', count: schedules.filter(s => s.status === 'paused').length },
                { key: 'postponed', label: 'Reportées', icon: '🔁', count: overrides.filter(o => o.override_type === 'postpone').length },
                { key: 'cancelled', label: 'Annulées', icon: '❌', count: schedules.filter(s => s.status === 'cancelled').length },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setInterventionSubTab(tab.key)}
                  className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    interventionSubTab === tab.key
                      ? 'bg-white text-gray-900 shadow-md'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                    interventionSubTab === tab.key
                      ? (tab.key === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')
                      : 'bg-gray-200 text-gray-500'
                  }`}>{tab.count}</span>
                </button>
              ))}
            </div>

            {/* Contenu: Terminées */}
            {interventionSubTab === 'completed' && (
              <div className="space-y-3">
                {schedules.filter(s => s.status === 'completed' || s.status === 'partially_completed').length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Aucune mission terminée</p>
                  </div>
                ) : (
                  schedules.filter(s => s.status === 'completed' || s.status === 'partially_completed')
                    .sort((a, b) => (b.completed_at || b.updated_at || '').localeCompare(a.completed_at || a.updated_at || ''))
                    .map(schedule => (
                    <div
                      key={schedule.id}
                      className="p-4 bg-green-50 border border-green-200 rounded-xl cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={() => { setSelectedSchedule(schedule); setShowScheduleDetail(true); }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-gray-900 truncate">{schedule.worksite_title || schedule.worksites?.title || 'Mission'}</p>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                schedule.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-teal-100 text-teal-800'
                              }`}>
                                {schedule.status === 'completed' ? '✓ Terminée' : '◐ Partielle'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 truncate">
                              {schedule.collaborator_first_name} {schedule.collaborator_last_name}
                              {schedule.client_address && ` • ${schedule.client_address}`}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                              {schedule.start_date && (
                                <span>📅 {new Date(schedule.start_date).toLocaleDateString('fr-FR')}</span>
                              )}
                              {schedule.completed_at && (
                                <span>✅ Clôturée le {new Date(schedule.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                              )}
                              {schedule.completed_by && (
                                <span>👤 par {schedule.completed_by}</span>
                              )}
                            </div>
                            {schedule.completion_report && (
                              <p className="text-xs text-gray-600 mt-1.5 line-clamp-2 italic">
                                💬 {schedule.completion_report.length > 120 ? schedule.completion_report.substring(0, 120) + '…' : schedule.completion_report}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 flex-shrink-0 ml-3">
                          Voir →
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Contenu: En Pause */}
            {interventionSubTab === 'paused' && (
              <div className="space-y-3">
                {schedules.filter(s => s.status === 'paused').length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Pause className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Aucune mission en pause</p>
                  </div>
                ) : (
                  schedules.filter(s => s.status === 'paused').map(schedule => (
                    <div key={schedule.id} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <Pause className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 truncate">{schedule.worksite_title || schedule.worksites?.title || 'Mission'}</p>
                          <p className="text-sm text-gray-600 truncate">
                            {schedule.collaborator_first_name} {schedule.collaborator_last_name}
                            {schedule.start_date && ` • ${new Date(schedule.start_date).toLocaleDateString('fr-FR')} → ${new Date(schedule.end_date || schedule.start_date).toLocaleDateString('fr-FR')}`}
                          </p>
                          {schedule.pause_reason && (
                            <p className="text-xs text-amber-700 mt-1">💬 {schedule.pause_reason}</p>
                          )}
                          {schedule.paused_at && (
                            <p className="text-xs text-gray-400 mt-0.5">Depuis le {new Date(schedule.paused_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                          )}
                          {schedule.pause_history && schedule.pause_history.length > 0 && (
                            <p className="text-xs text-gray-400 mt-0.5">🔄 {schedule.pause_history.length} cycle{schedule.pause_history.length > 1 ? 's' : ''} précédent{schedule.pause_history.length > 1 ? 's' : ''}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setResumeSchedule(schedule);
                          setResumeData({
                            start_date: '',
                            time: schedule.time || '',
                            team_leader_id: schedule.team_leader_id || '',
                            collaborator_id: schedule.collaborator_id || ''
                          });
                          setShowResumeModal(true);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-4 py-2 text-sm flex items-center space-x-1.5 flex-shrink-0 ml-3"
                      >
                        <Play className="w-4 h-4" />
                        <span>Reprendre</span>
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Contenu: Reportées */}
            {interventionSubTab === 'postponed' && (
              <div className="space-y-3">
                {overrides.filter(o => o.override_type === 'postpone').length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Aucune journée reportée</p>
                  </div>
                ) : (
                  overrides.filter(o => o.override_type === 'postpone').map(ov => (
                    <div key={ov.id} className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-lg">🔁</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 truncate">
                            {ov.schedules?.worksite_title || 'Mission'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {ov.schedules?.collaborator_first_name} {ov.schedules?.collaborator_last_name}
                          </p>
                          <p className="text-sm text-blue-700 mt-1">
                            📅 {new Date(ov.original_date).toLocaleDateString('fr-FR')} → {ov.new_date ? new Date(ov.new_date).toLocaleDateString('fr-FR') : <span className="text-amber-600 font-semibold">À programmer</span>}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">💬 {ov.reason}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                        {!ov.new_date && (
                          <Button
                            onClick={() => {
                              setRescheduleOverride(ov);
                              setRescheduleDate(ov.new_date || '');
                              setShowRescheduleModal(true);
                            }}
                            variant="outline"
                            className="rounded-xl px-3 py-2 text-sm flex items-center space-x-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                          >
                            <Calendar className="w-3.5 h-3.5" />
                            <span>Reprogrammer</span>
                          </Button>
                        )}
                        <Button
                          onClick={async () => {
                            if (!(await confirm('Annuler ce report ?'))) return;
                            try {
                              const token = localStorage.getItem('token');
                              await axios.delete(`${API}/planning-overrides/${ov.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Report annulé');
                              setOverrides(prev => prev.filter(o => o.id !== ov.id));
                            } catch (err) {
                              toast.error('Erreur : ' + (err.response?.data?.detail || err.message));
                            }
                          }}
                          variant="outline"
                          className="rounded-xl px-3 py-2 text-sm flex items-center space-x-1 border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Annuler</span>
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Contenu: Annulées */}
            {interventionSubTab === 'cancelled' && (
              <div className="space-y-3">
                {schedules.filter(s => s.status === 'cancelled').length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <X className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Aucune mission annulée</p>
                  </div>
                ) : (
                  schedules.filter(s => s.status === 'cancelled').map(schedule => (
                    <div key={schedule.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-xl">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <X className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 truncate">{schedule.worksite_title || schedule.worksites?.title || 'Mission'}</p>
                          <p className="text-sm text-gray-600 truncate">
                            {schedule.collaborator_first_name} {schedule.collaborator_last_name}
                            {schedule.start_date && ` • ${new Date(schedule.start_date).toLocaleDateString('fr-FR')} → ${new Date(schedule.end_date || schedule.start_date).toLocaleDateString('fr-FR')}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modals for Forms */}
      
      {/* Team Leader Form Modal */}
      {showTeamLeaderForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <Card className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full mx-4">
            <CardHeader className="bg-gradient-to-r from-gray-900 to-black text-white rounded-t-3xl pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold mb-1">👥 Nouveau Chef d'équipe</CardTitle>
                  <p className="text-gray-300 text-sm">Sélectionnez un membre invité pour en faire un chef d'équipe</p>
                </div>
                <Button
                  onClick={() => {
                    setShowTeamLeaderForm(false);
                    setSelectedExistingUser(null);
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-xl p-4">
                <p className="text-sm text-blue-800 font-medium">
                  ℹ️ Seuls les membres invités peuvent devenir chefs d'équipe
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Utilisez le bouton "+ Collaborateur" pour inviter de nouveaux membres
                </p>
              </div>

              {/* Liste des utilisateurs disponibles */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  👤 Sélectionnez un membre
                </label>
                <div className="grid gap-3 max-h-96 overflow-y-auto">
                  {collaborators
                    .filter(c => !teamLeaders.find(tl => tl.user_id === c.id)) // Exclure ceux déjà chefs d'équipe
                    .filter(c => {
                      // Ne garder que les utilisateurs invités par email (avec un vrai email)
                      // Exclure les utilisateurs créés manuellement avec email temporaire
                      const hasValidEmail = c.email && c.email.includes('@');
                      const isNotTemporaryEmail = !c.email?.includes('@temp-skyapp.local');
                      const isNotVirtual = !c.is_virtual;
                      
                      // Debug: afficher tous les utilisateurs filtrés
                      if (hasValidEmail && !isNotTemporaryEmail) {
                        console.log(`❌ Exclu (email temporaire): ${c.first_name} ${c.last_name} - ${c.email}`);
                      }
                      
                      return hasValidEmail && isNotTemporaryEmail && isNotVirtual;
                    })
                    .map(collab => {
                      const badgeColors = {
                        'ADMIN': 'bg-purple-100 text-purple-800 border-purple-300',
                        'BUREAU': 'bg-blue-100 text-blue-800 border-blue-300',
                        'TECHNICIEN': 'bg-green-100 text-green-800 border-green-300'
                      };
                      const badgeColor = badgeColors[collab.role] || 'bg-gray-100 text-gray-800 border-gray-300';
                      
                      return (
                        <div
                          key={collab.id}
                          onClick={() => {
                            setSelectedExistingUser(collab.id);
                            setTeamLeaderData({
                              nom: collab.last_name || collab.nom || '',
                              prenom: collab.first_name || collab.prenom || '',
                              email: collab.email || '',
                              telephone: collab.phone || collab.telephone || '',
                              specialite: 'Supervision',
                              couleur: '#3B82F6'
                            });
                          }}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                            selectedExistingUser === collab.id
                              ? 'border-black bg-gray-50 shadow-md'
                              : 'border-gray-200 hover:border-gray-400 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                                selectedExistingUser === collab.id ? 'bg-black text-white' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {(collab.first_name || collab.prenom || 'U')[0]}{(collab.last_name || collab.nom || 'U')[0]}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {collab.first_name || collab.prenom} {collab.last_name || collab.nom}
                                </p>
                                <p className="text-sm text-gray-600">{collab.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold border ${badgeColor}`}>
                                {collab.role}
                              </span>
                              {selectedExistingUser === collab.id && (
                                <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
                                  <span className="text-white text-lg">✓</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {collaborators.filter(c => !teamLeaders.find(tl => tl.user_id === c.id)).length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <p className="text-gray-500 font-medium">Aucun membre disponible</p>
                  <p className="text-sm text-gray-400 mt-1">Tous les membres ont déjà été assignés comme chefs d'équipe</p>
                </div>
              )}
              
              <div className="flex space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => {
                  setShowTeamLeaderForm(false);
                  setSelectedExistingUser(null);
                }} className="flex-1 rounded-xl py-3">
                  Annuler
                </Button>
                <Button 
                  onClick={addTeamLeader} 
                  disabled={!selectedExistingUser}
                  className="flex-1 bg-gradient-to-r from-gray-900 to-black hover:from-black hover:to-gray-800 text-white rounded-xl py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter Chef d'équipe
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Collaborator Form Modal */}
      {showCollaboratorForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold text-gray-900">Nouveau Collaborateur</CardTitle>
                <Button
                  onClick={() => setShowCollaboratorForm(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Prénom *"
                  value={collaboratorData.prenom}
                  onChange={(e) => setCollaboratorData(prev => ({...prev, prenom: e.target.value}))}
                  className="rounded-xl border-gray-200"
                />
                <Input
                  placeholder="Nom *"
                  value={collaboratorData.nom}
                  onChange={(e) => setCollaboratorData(prev => ({...prev, nom: e.target.value}))}
                  className="rounded-xl border-gray-200"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chef d'équipe (optionnel)
                </label>
                <select
                  value={collaboratorData.team_leader_id}
                  onChange={(e) => setCollaboratorData(prev => ({...prev, team_leader_id: e.target.value}))}
                  className="w-full rounded-xl border border-gray-200 focus:border-gray-500 focus:ring-2 focus:ring-gray-500/20 p-3"
                >
                  <option value="">Non assigné - Je l'assignerai plus tard dans Équipes</option>
                  {teamLeaders.map(leader => (
                    <option key={leader.id} value={leader.id}>
                      {leader.prenom} {leader.nom} - {leader.specialite}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Vous pourrez assigner ce collaborateur à un chef d'équipe depuis l'onglet Équipes
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-xs text-blue-700">
                  ℹ️ Ce collaborateur n'aura pas de compte Skyapp. Il sera uniquement visible dans la gestion du planning.
                </p>
              </div>
              
              <Input
                placeholder="Téléphone (optionnel)"
                value={collaboratorData.telephone}
                onChange={(e) => setCollaboratorData(prev => ({...prev, telephone: e.target.value}))}
                className="rounded-xl border-gray-200"
              />
              
              <div className="flex space-x-3 pt-4">
                <Button variant="outline" onClick={() => setShowCollaboratorForm(false)} className="flex-1">
                  Annuler
                </Button>
                <Button onClick={addCollaborator} className="flex-1 bg-gray-700 hover:bg-gray-800 text-white">
                  Ajouter Collaborateur
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Collaborator Modal */}
      {showEditCollaboratorModal && editingCollaborator && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold text-gray-900">
                  Modifier le collaborateur
                </CardTitle>
                <Button
                  onClick={() => setShowEditCollaboratorModal(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEditCollaborator} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <Input
                      placeholder="Prénom"
                      value={editCollaboratorData.first_name}
                      onChange={(e) => setEditCollaboratorData(prev => ({...prev, first_name: e.target.value}))}
                      className="rounded-xl border-gray-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <Input
                      placeholder="Nom"
                      value={editCollaboratorData.last_name}
                      onChange={(e) => setEditCollaboratorData(prev => ({...prev, last_name: e.target.value}))}
                      className="rounded-xl border-gray-200"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <Input
                    placeholder="Téléphone"
                    value={editCollaboratorData.phone}
                    onChange={(e) => setEditCollaboratorData(prev => ({...prev, phone: e.target.value}))}
                    className="rounded-xl border-gray-200"
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => setShowEditCollaboratorModal(false)} 
                    className="flex-1 rounded-xl"
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  >
                    Enregistrer
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule Form Modal */}
      {showScheduleForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader className="pb-6 bg-gradient-to-r from-[#1e3a8a] via-[#2563EB] to-[#7C3AED] text-white rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold mb-1">📅 Nouveau Planning</CardTitle>
                  <p className="text-blue-100 text-sm">Créez une nouvelle intervention pour votre équipe</p>
                </div>
                <Button
                  onClick={() => setShowScheduleForm(false)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Navigation Bar - Type d'intervention */}
              <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-2xl p-2 flex gap-2">
                <button
                  onClick={() => setScheduleData(prev => ({
                    ...prev,
                    intervention_category: 'worksite',
                    client_name: '',
                    client_address: '',
                    client_contact: '',
                    worksite_id: prev.intervention_category !== 'worksite' ? '' : prev.worksite_id
                  }))}
                  className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all ${
                    scheduleData.intervention_category === 'worksite'
                      ? 'bg-white text-[#2563EB] shadow-lg scale-105'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  🏗️ Chantier
                </button>
                <button
                  onClick={() => setScheduleData(prev => ({
                    ...prev,
                    intervention_category: 'rdv',
                    worksite_id: '',
                    client_name: prev.intervention_category !== 'rdv' ? '' : prev.client_name,
                    client_address: prev.intervention_category !== 'rdv' ? '' : prev.client_address,
                    client_contact: prev.intervention_category !== 'rdv' ? '' : prev.client_contact
                  }))}
                  className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all ${
                    scheduleData.intervention_category === 'rdv'
                      ? 'bg-white text-[#2563EB] shadow-lg scale-105'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  📅 Rendez-vous
                </button>
                <button
                  onClick={() => setScheduleData(prev => ({
                    ...prev,
                    intervention_category: 'urgence',
                    worksite_id: '',
                    client_name: prev.intervention_category !== 'urgence' ? '' : prev.client_name,
                    client_address: prev.intervention_category !== 'urgence' ? '' : prev.client_address,
                    client_contact: prev.intervention_category !== 'urgence' ? '' : prev.client_contact
                  }))}
                  className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all ${
                    scheduleData.intervention_category === 'urgence'
                      ? 'bg-white text-red-600 shadow-lg scale-105'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  🚨 Urgence
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              {/* Chantier ou Informations Client */}
              {scheduleData.intervention_category === 'worksite' ? (
                <div className="space-y-4">
                  {/* Sous-onglets Chantier Existant / Nouveau Chantier */}
                  <div className="flex gap-2 bg-gray-100 rounded-xl p-1">
                    <button
                      onClick={() => setScheduleData(prev => ({...prev, worksite_mode: 'existing', new_worksite_title: '', new_worksite_address: ''}))}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                        scheduleData.worksite_mode === 'existing'
                          ? 'bg-white text-[#2563EB] shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      📁 Chantier existant
                    </button>
                    <button
                      onClick={() => setScheduleData(prev => ({...prev, worksite_mode: 'new', worksite_id: ''}))}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                        scheduleData.worksite_mode === 'new'
                          ? 'bg-white text-[#2563EB] shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      ➕ Nouveau chantier
                    </button>
                  </div>

                  {scheduleData.worksite_mode === 'existing' ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        🏗️ Sélectionnez le Chantier
                      </label>
                      <select
                        value={scheduleData.worksite_id}
                        onChange={(e) => setScheduleData(prev => ({...prev, worksite_id: e.target.value}))}
                        className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                      >
                        <option value="">Choisissez un chantier *</option>
                        {worksites.map(worksite => {
                          const hasSchedules = schedules.some(s => s.worksite_id === worksite.id);
                          return (
                            <option key={worksite.id} value={worksite.id}>
                              {hasSchedules ? '📅 ' : ''}{worksite.title}
                            </option>
                          );
                        })}
                      </select>
                      <p className="text-xs text-gray-500 mt-2">
                        📅 = Chantier déjà planifié
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          🏗️ Nom du chantier *
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Rénovation cuisine M. Dupont"
                          value={scheduleData.new_worksite_title}
                          onChange={(e) => setScheduleData(prev => ({...prev, new_worksite_title: e.target.value}))}
                          className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          📍 Adresse du chantier
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: 12 rue de la Paix, 75001 Paris"
                          value={scheduleData.new_worksite_address}
                          onChange={(e) => setScheduleData(prev => ({...prev, new_worksite_address: e.target.value}))}
                          className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      👤 Nom du Client *
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Dupont SA"
                      value={scheduleData.client_name}
                      onChange={(e) => setScheduleData(prev => ({...prev, client_name: e.target.value}))}
                      className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      📍 Adresse d'Intervention *
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: 123 Rue de la République, 75001 Paris"
                      value={scheduleData.client_address}
                      onChange={(e) => setScheduleData(prev => ({...prev, client_address: e.target.value}))}
                      className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      📞 Coordonnées du Client
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: 06 12 34 56 78 ou contact@client.fr"
                      value={scheduleData.client_contact}
                      onChange={(e) => setScheduleData(prev => ({...prev, client_contact: e.target.value}))}
                      className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                    />
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    👤 Chef d'équipe {scheduleData.intervention_category === 'rdv' ? '(optionnel)' : '*'}
                  </label>
                  <select
                    value={scheduleData.team_leader_id}
                    onChange={async (e) => {
                      const teamLeaderId = e.target.value;
                      
                      // Auto-sélectionner TOUS les collaborateurs de cette équipe
                      if (teamLeaderId) {
                        try {
                          const token = localStorage.getItem('token');
                          const headers = { 'Authorization': `Bearer ${token}` };
                          
                          // Récupérer les collaborateurs de ce chef d'équipe via l'API
                          const response = await axios.get(`${API}/team-leaders/${teamLeaderId}/collaborators`, { headers });
                          console.log('📦 [AutoSelect] Réponse API collaborateurs:', response.data);
                          
                          // Extraire TOUS les IDs des collaborateurs
                          const allCollaboratorIds = [];
                          if (response.data && response.data.length > 0) {
                            response.data.forEach(item => {
                              let collabId = '';
                              // La réponse peut avoir une structure imbriquée
                              if (item.collaborator && item.collaborator.id) {
                                collabId = item.collaborator.id;
                              } else if (item.collaborator_id) {
                                collabId = item.collaborator_id;
                              } else if (item.id) {
                                collabId = item.id;
                              }
                              if (collabId) {
                                allCollaboratorIds.push(collabId);
                              }
                            });
                          }
                          
                          // Le premier devient le collaborateur principal
                          const firstCollaboratorId = allCollaboratorIds[0] || '';
                          // Les autres deviennent des collaborateurs supplémentaires
                          const additionalCollaborators = allCollaboratorIds.slice(1);
                          
                          console.log('🎯 [AutoSelect] Premier collaborateur:', firstCollaboratorId);
                          console.log('👥 [AutoSelect] Collaborateurs supplémentaires:', additionalCollaborators);
                          
                          setScheduleData(prev => ({
                            ...prev,
                            team_leader_id: teamLeaderId,
                            collaborator_id: firstCollaboratorId,
                            additional_collaborators: additionalCollaborators
                          }));
                        } catch (error) {
                          console.error('❌ [AutoSelect] Erreur chargement collaborateurs:', error);
                          setScheduleData(prev => ({
                            ...prev,
                            team_leader_id: teamLeaderId,
                            collaborator_id: '',
                            additional_collaborators: []
                          }));
                        }
                      } else {
                        setScheduleData(prev => ({
                          ...prev,
                          team_leader_id: '',
                          collaborator_id: '',
                          additional_collaborators: []
                        }));
                      }
                    }}
                    className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                  >
                    <option value="">{scheduleData.intervention_category === 'rdv' ? 'Sélectionner (optionnel)' : 'Sélectionner *'}</option>
                    {teamLeaders.map(leader => (
                      <option key={leader.id} value={leader.id}>
                        {leader.prenom} {leader.nom}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    👥 Collaborateur principal {scheduleData.intervention_category === 'rdv' ? '(optionnel)' : '*'}
                  </label>
                  <select
                    value={scheduleData.collaborator_id}
                    onChange={(e) => setScheduleData(prev => ({...prev, collaborator_id: e.target.value}))}
                    className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                  >
                    <option value="">
                      {scheduleData.intervention_category === 'rdv' ? 'Sélectionner (optionnel)' : 'Sélectionner *'}
                    </option>
                    {collaborators.map(collaborator => (
                      <option key={collaborator.id} value={collaborator.id}>
                        {collaborator.first_name || collaborator.prenom} {collaborator.last_name || collaborator.nom}
                      </option>
                    ))}
                  </select>
                  {scheduleData.team_leader_id && scheduleData.intervention_category === 'worksite' && (
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Le premier collaborateur de l'équipe a été sélectionné automatiquement
                    </p>
                  )}
                </div>

                {/* NOUVEAU : Sélection multiple de collaborateurs supplémentaires */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    ➕ Collaborateurs supplémentaires (optionnel)
                  </label>
                  <div className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50 max-h-48 overflow-y-auto">
                    {collaborators.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">Aucun collaborateur disponible</p>
                    ) : (
                      <div className="space-y-2">
                        {collaborators
                          .filter(c => c.id !== scheduleData.collaborator_id) // Exclure le collaborateur principal
                          .map(collaborator => (
                          <label 
                            key={collaborator.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-white cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={scheduleData.additional_collaborators.includes(collaborator.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setScheduleData(prev => ({
                                    ...prev,
                                    additional_collaborators: [...prev.additional_collaborators, collaborator.id]
                                  }));
                                } else {
                                  setScheduleData(prev => ({
                                    ...prev,
                                    additional_collaborators: prev.additional_collaborators.filter(id => id !== collaborator.id)
                                  }));
                                }
                              }}
                              className="w-4 h-4 text-[#2563EB] border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">
                              {collaborator.first_name || collaborator.prenom} {collaborator.last_name || collaborator.nom}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {scheduleData.additional_collaborators.length > 0 && (
                    <p className="text-xs text-purple-600 mt-2">
                      ✓ {scheduleData.additional_collaborators.length} collaborateur(s) supplémentaire(s) sélectionné(s)
                    </p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    📅 Date de début
                  </label>
                  <input
                    type="date"
                    value={scheduleData.start_date}
                    onChange={(e) => setScheduleData(prev => ({
                      ...prev, 
                      start_date: e.target.value,
                      end_date: prev.end_date < e.target.value ? e.target.value : prev.end_date
                    }))}
                    className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    📅 Date de fin
                  </label>
                  <input
                    type="date"
                    value={scheduleData.end_date}
                    min={scheduleData.start_date}
                    onChange={(e) => setScheduleData(prev => ({...prev, end_date: e.target.value}))}
                    className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    ⏰ Heure de début
                  </label>
                  <input
                    type="time"
                    value={scheduleData.time}
                    onChange={(e) => setScheduleData(prev => ({...prev, time: e.target.value}))}
                    className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  🕒 Durée de l'intervention
                </label>
                <select
                  value={scheduleData.shift}
                  onChange={(e) => setScheduleData(prev => ({...prev, shift: e.target.value}))}
                  className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 bg-white shadow-sm transition-all"
                >
                  {SHIFT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {scheduleData.shift === 'night' && nightSurcharge?.enabled && (
                  <div className="mt-2 flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2 text-sm text-indigo-800">
                    🌙 <span className="font-semibold">{nightSurcharge.label}</span>
                    <span className="font-bold">{formatNightSurcharge(nightSurcharge)}</span>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  📝 Description (optionnel)
                </label>
                <textarea
                  placeholder="Détails sur l'intervention prévue..."
                  value={scheduleData.description}
                  onChange={(e) => setScheduleData(prev => ({...prev, description: e.target.value}))}
                  rows={3}
                  className="w-full rounded-xl border border-[rgba(15,23,42,0.12)] focus:border-[#2563EB] focus:ring-2 focus:ring-blue-500/20 p-3 resize-none bg-white shadow-sm transition-all"
                />
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-5 border-2 border-purple-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 bg-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">✓</span>
                  </div>
                  <h4 className="font-bold text-purple-900 text-lg">Résumé du planning</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-purple-900 min-w-[110px] flex-shrink-0">🚨 Type :</span>
                    <span className="text-purple-700 flex-1">
                      {scheduleData.intervention_category === 'urgence' ? '🚨 Urgence' : 
                       scheduleData.intervention_category === 'worksite' ? '🏗️ Chantier' : 
                       '📅 Rendez-vous'}
                    </span>
                  </div>
                  {scheduleData.intervention_category === 'worksite' && scheduleData.worksite_id && (
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-purple-900 min-w-[110px] flex-shrink-0">🏗️ Chantier :</span>
                      <span className="text-purple-700 flex-1">{getWorksiteName(scheduleData.worksite_id)}</span>
                    </div>
                  )}
                  {scheduleData.intervention_category !== 'worksite' && scheduleData.client_name && (
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-purple-900 min-w-[110px] flex-shrink-0">👤 Client :</span>
                      <span className="text-purple-700 flex-1">{scheduleData.client_name}</span>
                    </div>
                  )}
                  {scheduleData.client_address && (
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-purple-900 min-w-[110px] flex-shrink-0">📍 Adresse :</span>
                      <span className="text-purple-700 flex-1">{scheduleData.client_address}</span>
                    </div>
                  )}
                  {scheduleData.client_contact && (
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-purple-900 min-w-[110px] flex-shrink-0">📞 Contact :</span>
                      <span className="text-purple-700 flex-1">{scheduleData.client_contact}</span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-purple-900 min-w-[110px] flex-shrink-0">� Équipe :</span>
                    <span className="text-purple-700 flex-1">{getTeamMembersDisplay(scheduleData.team_leader_id, scheduleData.collaborator_id)}</span>
                  </div>
                  {scheduleData.collaborator_id && (
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-purple-900 min-w-[110px] flex-shrink-0">👤 Collaborateur :</span>
                      <span className="text-purple-700 flex-1">{getCollaboratorName(scheduleData.collaborator_id)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-purple-900 min-w-[110px]">🕒 Horaire :</span>
                    <span className="text-purple-700">{getShiftLabel(scheduleData.shift)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-purple-900 min-w-[110px]">📅 Période :</span>
                    <span className="text-purple-700">
                      {scheduleData.start_date ? new Date(scheduleData.start_date).toLocaleDateString('fr-FR') : 'Non définie'}
                      {scheduleData.end_date && scheduleData.end_date !== scheduleData.start_date 
                        ? ` → ${new Date(scheduleData.end_date).toLocaleDateString('fr-FR')}`
                        : ''}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-4 pt-6 border-t-2 border-gray-100">
                <Button 
                  variant="outline" 
                  onClick={() => setShowScheduleForm(false)} 
                  disabled={saving}
                  className="flex-1 rounded-xl py-3 border-2 hover:bg-gray-50 font-semibold transition-all"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={addSchedule} 
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl py-3 font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>✨ Créer Planning</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* NEW: Day Detail Modal */}
      {showDayDetail && selectedDayData && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Planning du {selectedDayData.date.toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h2>
                  <p className="text-gray-600 mt-1">
                    Détail complet de la journée • {selectedDayData.totalHours}h programmées • {selectedDayData.availableSlots} créneaux libres
                  </p>
                </div>
                <Button
                  onClick={() => setShowDayDetail(false)}
                  variant="outline"
                  className="rounded-xl px-4 py-2"
                >
                  <X className="h-4 w-4 mr-2" />
                  Fermer
                </Button>
              </div>

              {/* Day Statistics */}
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{selectedDayData.schedules.length}</div>
                  <div className="text-sm text-blue-700">Interventions</div>
                </div>
                <div className="bg-purple-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{selectedDayData.teams.length}</div>
                  <div className="text-sm text-purple-700">Équipes</div>
                </div>
                <div className="bg-green-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{selectedDayData.collaborators.length}</div>
                  <div className="text-sm text-green-700">Collaborateurs</div>
                </div>
                <div className="bg-orange-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{selectedDayData.totalHours}h</div>
                  <div className="text-sm text-orange-700">Total Heures</div>
                </div>
              </div>

              {/* Interventions Details - Grouped by Team */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Interventions Programmées</h3>
                  {selectedDayData.schedules.length === 0 ? (
                    <div className="bg-gray-50 rounded-2xl p-8 text-center">
                      <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-gray-600">Aucune intervention programmée pour cette journée</p>
                      <Button
                        onClick={() => {
                          setShowDayDetail(false);
                          setShowScheduleForm(true);
                        }}
                        className="mt-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-6 py-2"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Programmer une intervention
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Group schedules by team leader */}
                      {(() => {
                        const schedulesByTeam = selectedDayData.schedules.reduce((acc, schedule) => {
                          const teamId = schedule.team_leader_id || 'unassigned';
                          if (!acc[teamId]) acc[teamId] = [];
                          acc[teamId].push(schedule);
                          return acc;
                        }, {});

                        return Object.entries(schedulesByTeam).map(([teamId, teamSchedules]) => {
                          const teamLeader = teamId !== 'unassigned' ? teamLeaderMap.get(teamId) : null;
                          const teamColor = teamLeader?.couleur || '#6B7280';
                          
                          return (
                            <div key={teamId} className="bg-gradient-to-br from-white to-gray-50 border-2 rounded-2xl shadow-lg overflow-hidden">
                              {/* Team Header */}
                              <div 
                                className="p-4 flex items-center space-x-4"
                                style={{ 
                                  background: `linear-gradient(135deg, ${teamColor}20, ${teamColor}10)`,
                                  borderBottom: `3px solid ${teamColor}`
                                }}
                              >
                                <div 
                                  className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg"
                                  style={{ backgroundColor: teamColor }}
                                >
                                  {teamLeader ? `${teamLeader.prenom?.charAt(0)}${teamLeader.nom?.charAt(0)}` : '?'}
                                </div>
                                <div className="flex-1">
                                  <h4 className="text-lg font-bold text-gray-900">
                                    {teamLeader ? `${teamLeader.prenom} ${teamLeader.nom}` : 
                                     (teamSchedules[0]?.collaborator_id ? getCollaboratorName(teamSchedules[0].collaborator_id) : 'Non assigné')}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    {teamLeader?.specialite || (teamSchedules[0]?.collaborator_id ? 'Collaborateur' : 'Aucune équipe')} • {teamSchedules.length} intervention{teamSchedules.length > 1 ? 's' : ''}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-bold" style={{ color: teamColor }}>
                                    {teamSchedules.reduce((sum, s) => sum + (parseInt(s.hours) || 0), 0)}h
                                  </div>
                                  <div className="text-xs text-gray-600">Total</div>
                                </div>
                              </div>

                              {/* Team Schedules */}
                              <div className="p-4 space-y-3">
                                {teamSchedules.map((schedule, idx) => (
                                  <div 
                                    key={schedule.id || idx} 
                                    className="bg-white border-2 rounded-xl p-4 hover:shadow-md transition-all"
                                    style={{ borderLeftWidth: '4px', borderLeftColor: teamColor }}
                                  >
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        {/* Time and Title */}
                                        <div className="flex items-center space-x-3 mb-3">
                                          <span 
                                            className="text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm"
                                            style={{ backgroundColor: teamColor }}
                                          >
                                            {schedule.time || '08:00'}
                                          </span>
                                          <h5 className="text-base font-bold text-gray-900">
                                            {schedule.intervention_category === 'worksite' && schedule.worksite_id 
                                              ? getWorksiteName(schedule.worksite_id)
                                              : (schedule.client_name || 'Client')
                                            }
                                          </h5>
                                          {schedule.intervention_category && (
                                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                              schedule.intervention_category === 'urgence' 
                                                ? 'bg-red-100 text-red-700' 
                                                : schedule.intervention_category === 'worksite'
                                                  ? 'bg-indigo-100 text-indigo-700'
                                                  : 'bg-blue-100 text-blue-700'
                                            }`}>
                                              {schedule.intervention_category === 'urgence' ? '🚨 Urgence' : 
                                               schedule.intervention_category === 'worksite' ? '🏗️ Chantier' :
                                               '📅 RDV'}
                                            </span>
                                          )}
                                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                            {getShiftLabel(schedule.shift)} - {schedule.hours}h
                                          </span>
                                          {schedule.night_surcharge_applied && (
                                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-semibold">
                                              🌙 {schedule.night_surcharge_label || 'Majoration nuit'} : {schedule.night_surcharge_type === 'fixed' ? `+${parseFloat(schedule.night_surcharge_value).toFixed(2)}€` : `+${schedule.night_surcharge_value}%`}
                                            </span>
                                          )}
                                        </div>

                                        {/* Details Grid */}
                                        <div className="grid grid-cols-2 gap-3 mb-3">
                                          {schedule.client_address && (
                                            <div className="bg-gray-50 rounded-lg p-2 col-span-2">
                                              <p className="text-xs text-gray-500 mb-1">📍 Adresse</p>
                                              <p className="text-sm font-semibold text-gray-900">
                                                {schedule.client_address}
                                              </p>
                                            </div>
                                          )}
                                          {schedule.client_contact && (
                                            <div className="bg-gray-50 rounded-lg p-2">
                                              <p className="text-xs text-gray-500 mb-1">📞 Contact</p>
                                              <p className="text-sm font-semibold text-gray-900">
                                                {schedule.client_contact}
                                              </p>
                                            </div>
                                          )}
                                          <div className="bg-gray-50 rounded-lg p-2">
                                            <p className="text-xs text-gray-500 mb-1">Collaborateur</p>
                                            <p className="text-sm font-semibold text-gray-900">
                                              {getCollaboratorName(schedule.collaborator_id)}
                                            </p>
                                          </div>
                                          <div className="bg-gray-50 rounded-lg p-2">
                                            <p className="text-xs text-gray-500 mb-1">Statut</p>
                                            <p className="text-sm font-semibold">
                                              <span className={
                                                schedule.status === 'completed' ? 'text-green-600' :
                                                schedule.status === 'partially_completed' ? 'text-teal-600' :
                                                schedule.status === 'in_progress' ? 'text-blue-600' :
                                                schedule.status === 'cancelled' ? 'text-red-600' :
                                                schedule.status === 'paused' ? 'text-amber-600' :
                                                'text-orange-600'
                                              }>
                                                {schedule.status === 'completed' ? '✓ Terminé' :
                                                 schedule.status === 'partially_completed' ? '◐ Partiellement terminé' :
                                                 schedule.status === 'in_progress' ? '⟳ En cours' :
                                                 schedule.status === 'cancelled' ? '✗ Annulé' :
                                                 schedule.status === 'paused' ? '⏸ En pause' :
                                                 '○ Planifié'}
                                              </span>
                                            </p>
                                          </div>
                                        </div>

                                        {/* Description */}
                                        {schedule.description && (
                                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                                            <p className="text-xs text-blue-800">
                                              <strong>Note:</strong> {schedule.description}
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      {/* Actions */}
                                      <div className="flex flex-col space-y-2 ml-4">
                                        <Button 
                                          size="sm" 
                                          variant="outline" 
                                          className="rounded-lg text-xs px-3 py-1.5"
                                          onClick={() => {
                                            setSelectedSchedule(schedule);
                                            setShowScheduleDetail(true);
                                            setScheduleData({
                                              worksite_id: schedule.worksite_id,
                                              team_leader_id: schedule.team_leader_id,
                                              collaborator_id: schedule.collaborator_id,
                                              date: schedule.start_date || schedule.date,
                                              time: schedule.time,
                                              shift: schedule.shift,
                                              hours: schedule.hours,
                                              description: schedule.description
                                            });
                                            setShowDayDetail(false);
                                            setShowScheduleForm(true);
                                          }}
                                        >
                                          <Edit className="h-3 w-3" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline" 
                                          className="rounded-lg text-xs border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5"
                                          onClick={() => deleteSchedule(schedule.id)}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>

                {/* Team Assignment Section */}
                {selectedDayData.teams.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Équipes Assignées</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedDayData.teams.map((team, index) => (
                        <div key={team.id || index} className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-4">
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: team.couleur }}
                            >
                              {team.prenom.charAt(0)}{team.nom.charAt(0)}
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">{team.prenom} {team.nom}</h4>
                              <p className="text-sm text-gray-600">{team.specialite}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions Rapides</h3>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => {
                        setShowDayDetail(false);
                        setShowScheduleForm(true);
                      }}
                      className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Nouvelle Intervention
                    </Button>
                    <Button
                      onClick={() => {
                        setShowDayDetail(false);
                        setShowTeamLeaderForm(true);
                      }}
                      variant="outline"
                      className="rounded-xl"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assigner Équipe
                    </Button>
                    <Button variant="outline" className="rounded-xl">
                      <Clock className="h-4 w-4 mr-2" />
                      Gestion Horaires
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Planification de Chantier */}
      {showPlanningModal && selectedWorksiteForPlanning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl mx-4 max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-1">📅 Planifier le Chantier</h2>
                  <p className="text-gray-300 text-sm">{selectedWorksiteForPlanning.title}</p>
                </div>
                <button
                  onClick={() => setShowPlanningModal(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Chef d'équipe */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  👤 Chef d'Équipe
                </label>
                <select
                  value={planningData.team_leader_id}
                  onChange={async (e) => {
                    const teamLeaderId = e.target.value;
                    
                    // Auto-sélectionner les collaborateurs de cette équipe
                    if (teamLeaderId) {
                      try {
                        const token = localStorage.getItem('token');
                        const headers = { 'Authorization': `Bearer ${token}` };
                        
                        // Récupérer les collaborateurs de ce chef d'équipe via l'API
                        const response = await axios.get(`${API}/team-leaders/${teamLeaderId}/collaborators`, { headers });
                        console.log('📦 Réponse API collaborateurs:', response.data);
                        
                        // Extraire les IDs des collaborateurs (gérer la structure imbriquée)
                        const teamCollaboratorIds = response.data.map(item => {
                          // La réponse peut avoir une structure imbriquée : item.collaborator.id ou item.collaborator_id
                          if (item.collaborator && item.collaborator.id) {
                            return item.collaborator.id;
                          } else if (item.collaborator_id) {
                            return item.collaborator_id;
                          } else if (item.id) {
                            return item.id;
                          }
                          return null;
                        }).filter(id => id !== null);
                        
                        console.log('🎯 Auto-sélection des collaborateurs pour le chef:', teamLeaderId, teamCollaboratorIds);
                        
                        setPlanningData(prev => ({
                          ...prev,
                          team_leader_id: teamLeaderId,
                          collaborator_ids: teamCollaboratorIds
                        }));
                      } catch (error) {
                        console.error('Erreur chargement collaborateurs équipe:', error);
                        setPlanningData(prev => ({
                          ...prev,
                          team_leader_id: teamLeaderId,
                          collaborator_ids: []
                        }));
                      }
                    } else {
                      setPlanningData(prev => ({
                        ...prev,
                        team_leader_id: '',
                        collaborator_ids: []
                      }));
                    }
                  }}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un chef d'équipe...</option>
                  {teamLeaders.map(leader => (
                    <option key={leader.id} value={leader.id}>
                      {leader.prenom} {leader.nom} - {leader.specialite}
                    </option>
                  ))}
                </select>
              </div>

              {/* Collaborateurs */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  👥 Collaborateurs (optionnel)
                </label>
                
                {/* Barre de recherche */}
                <div className="mb-3">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="🔍 Rechercher un collaborateur..."
                      value={collaboratorSearch}
                      onChange={(e) => setCollaboratorSearch(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 pl-10 focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      🔍
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 max-h-64 overflow-y-auto border-2 border-gray-200">
                  {collaborators.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">Aucun collaborateur disponible</p>
                  ) : (
                    <>
                      {/* Debug: afficher les données brutes */}
                      {console.log('🔍 Collaborateurs à afficher:', collaborators)}
                      {collaborators
                        .filter(collab => {
                          const searchLower = collaboratorSearch.toLowerCase();
                          // Gérer les différents formats de nom possibles
                          const prenom = collab.prenom || collab.first_name || '';
                          const nom = collab.nom || collab.last_name || collab.name || '';
                          const email = collab.email || '';
                          const fullName = `${prenom} ${nom}`.toLowerCase();
                          const competences = (collab.competences || collab.skills || '').toLowerCase();
                          return fullName.includes(searchLower) || competences.includes(searchLower) || email.includes(searchLower);
                        })
                        .map(collab => {
                          // Gérer les différents formats de nom
                          const prenom = collab.prenom || collab.first_name || '';
                          const nom = collab.nom || collab.last_name || collab.name || '';
                          const displayName = prenom && nom ? `${prenom} ${nom}` : (collab.email || 'Utilisateur');
                          const competences = collab.competences || collab.skills || '';
                          
                          return (
                            <label key={collab.id} className="flex items-center space-x-3 p-3 hover:bg-white rounded-lg cursor-pointer transition-colors border border-transparent hover:border-gray-300">
                              <input
                                type="checkbox"
                                checked={planningData.collaborator_ids.includes(collab.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setPlanningData(prev => ({
                                      ...prev,
                                      collaborator_ids: [...prev.collaborator_ids, collab.id]
                                    }));
                                  } else {
                                    setPlanningData(prev => ({
                                      ...prev,
                                      collaborator_ids: prev.collaborator_ids.filter(id => id !== collab.id)
                                    }));
                                  }
                                }}
                                className="w-5 h-5 text-gray-800 rounded focus:ring-gray-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-gray-900">{displayName}</div>
                                <div className="text-xs text-gray-500">{collab.email}</div>
                                {competences && (
                                  <div className="text-xs text-gray-600">{competences}</div>
                                )}
                              </div>
                            </label>
                          );
                        })
                      }
                    </>
                  )}
                </div>
              </div>

              {/* Type d'intervention */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  ⏰ Type d'intervention
                </label>
                <select
                  value={planningData.shift_type}
                  onChange={(e) => setPlanningData(prev => ({...prev, shift_type: e.target.value}))}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white shadow-sm"
                >
                  {SHIFT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {planningData.shift_type === 'night' && nightSurcharge?.enabled && (
                  <div className="mt-2 flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2 text-sm text-indigo-800">
                    🌙 <span className="font-semibold">{nightSurcharge.label}</span>
                    <span className="font-bold">{formatNightSurcharge(nightSurcharge)}</span>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    📅 Date de début
                  </label>
                  <input
                    type="date"
                    value={planningData.start_date}
                    onChange={(e) => setPlanningData(prev => ({...prev, start_date: e.target.value}))}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    📅 Date de fin
                  </label>
                  <input
                    type="date"
                    value={planningData.end_date}
                    onChange={(e) => setPlanningData(prev => ({...prev, end_date: e.target.value}))}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📝 Notes (optionnel)
                </label>
                <textarea
                  value={planningData.notes}
                  onChange={(e) => setPlanningData(prev => ({...prev, notes: e.target.value}))}
                  placeholder="Ajouter des notes pour l'équipe..."
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
                  rows="3"
                />
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4 border-t">
                <Button
                  onClick={() => setShowPlanningModal(false)}
                  variant="outline"
                  className="flex-1 rounded-xl py-3"
                >
                  Annuler
                </Button>
                <Button
                  disabled={saving}
                  onClick={async () => {
                    try {
                      if (!planningData.team_leader_id) {
                        toast.warning('Veuillez sélectionner un chef d\'équipe');
                        return;
                      }
                      
                      if (!planningData.start_date) {
                        toast.warning('Veuillez sélectionner une date de début');
                        return;
                      }
                      
                      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
                      
                      console.log('📋 Données de planification:', planningData);
                      console.log('👥 Collaborateurs sélectionnés:', planningData.collaborator_ids);
                      
                      // Récupérer le chef d'équipe sélectionné
                      const selectedLeader = teamLeaders.find(tl => tl.id === planningData.team_leader_id);
                      
                      console.log('👤 Chef d\'équipe:', selectedLeader);
                      console.log('👤 Is virtual:', selectedLeader?.is_virtual);
                      
                      if (selectedLeader?.is_virtual) {
                        toast.warning('Ce chef d\'équipe est virtuel. Veuillez l\'inviter en tant qu\'utilisateur avant de planifier.');
                        return;
                      }
                      
                      // Si aucun collaborateur n'est sélectionné, vérifier qu'on a au moins le user_id du chef
                      const teamLeaderUserId = selectedLeader?.user_id;
                      const collaboratorsToSchedule = planningData.collaborator_ids.length > 0 
                        ? planningData.collaborator_ids 
                        : (teamLeaderUserId ? [teamLeaderUserId] : []); // Utiliser le user_id du chef comme collaborateur si disponible
                      
                      if (collaboratorsToSchedule.length === 0) {
                        toast.warning('Veuillez sélectionner au moins un collaborateur');
                        return;
                      }
                      
                      console.log('📅 Collaborateurs à planifier:', collaboratorsToSchedule);
                      setSaving(true);
                      
                      // Créer un schedule pour chaque collaborateur sélectionné ET pour chaque jour entre start_date et end_date
                      const selectedShiftOption = SHIFT_OPTIONS.find(s => s.value === planningData.shift_type) || SHIFT_OPTIONS[0];
                      
                      // Un seul schedule par collaborateur couvrant toute la période
                      // (period_start/period_end) → la mission s'affiche en bloc continu
                      // au lieu d'une carte séparée pour chaque jour.
                      const periodStart = planningData.start_date;
                      const periodEnd = planningData.end_date || planningData.start_date;

                      console.log(`📅 Création d'un schedule par collaborateur du ${periodStart} au ${periodEnd}`);

                      const schedulePromises = [];

                      for (const collabId of collaboratorsToSchedule) {
                        const scheduleData = {
                          worksite_id: selectedWorksiteForPlanning.id,
                          team_leader_id: planningData.team_leader_id,
                          collaborator_id: collabId,
                          period_start: periodStart,
                          period_end: periodEnd,
                          time: planningData.shift_type === 'night' ? '20:00' : planningData.shift_type === 'afternoon' ? '14:00' : '08:00',
                          shift: planningData.shift_type,
                          hours: selectedShiftOption.hours,
                          description: planningData.notes || ''
                        };

                        schedulePromises.push(axios.post(`${API}/schedules`, scheduleData, { headers }));
                      }
                      
                      // Attendre que tous les schedules soient créés
                      const results = await Promise.allSettled(schedulePromises);
                      
                      // Vérifier les échecs
                      const failed = results.filter(r => r.status === 'rejected');
                      const succeeded = results.filter(r => r.status === 'fulfilled');
                      
                      // Vérifier si des doublons ont été détectés
                      const duplicates = failed.filter(f => f.reason?.response?.status === 409);
                      const realErrors = failed.filter(f => f.reason?.response?.status !== 409);

                      // Logger toutes les erreurs réelles pour faciliter le debug
                      if (realErrors.length > 0) {
                        realErrors.forEach((f, idx) => {
                          console.error(`❌ Échec création schedule #${idx}:`, {
                            status: f.reason?.response?.status,
                            detail: f.reason?.response?.data?.detail,
                            data: f.reason?.response?.data,
                            message: f.reason?.message,
                          });
                        });
                      }

                      if (duplicates.length > 0 && succeeded.length === 0 && realErrors.length === 0) {
                        toast.warning('Tous les plannings existent déjà pour ces dates. Vérifiez le calendrier.');
                        return;
                      }

                      // Si aucune création n'a réussi et qu'au moins une erreur réelle est survenue, afficher le vrai message
                      if (succeeded.length === 0 && realErrors.length > 0) {
                        const first = realErrors[0];
                        const detail = first.reason?.response?.data?.detail || first.reason?.message || 'Erreur inconnue';
                        const status = first.reason?.response?.status || '';
                        toast.error(`Échec planification (${status}) : ${detail}`);
                        return;
                      }

                      console.log('✅ Schedules créés:', succeeded.map(r => r.value.data));
                      
                      // Mettre à jour le chantier
                      await axios.put(`${API}/worksites/${selectedWorksiteForPlanning.id}`, {
                        team_leader_id: planningData.team_leader_id,
                        start_date: planningData.start_date,
                        end_date: planningData.end_date,
                        notes: planningData.notes,
                        status: 'IN_PROGRESS'
                      }, { headers });

                      // Mise à jour optimiste : ajouter les nouveaux schedules + mettre à jour le chantier
                      // (déduplication par id : le Realtime peut déjà les avoir insérés)
                      const newSchedules = succeeded.map(r => r.value.data);
                      setSchedules(prev => {
                        const seen = new Set(prev.map(s => s.id));
                        return [...prev, ...newSchedules.filter(s => s && !seen.has(s.id))];
                      });
                      setWorksites(prev => prev.map(w =>
                        w.id === selectedWorksiteForPlanning.id
                          ? { ...w, status: 'IN_PROGRESS', team_leader_id: planningData.team_leader_id }
                          : w
                      ));

                      if (duplicates.length > 0) {
                        toast.success(`${succeeded.length} intervention(s) créée(s) (${duplicates.length} doublon(s) ignoré(s))`);
                      } else {
                        toast.success(`Chantier planifié ! ${succeeded.length} intervention(s) créée(s)`);
                      }
                      // Avertir si des erreurs réelles partielles
                      if (realErrors.length > 0) {
                        const first = realErrors[0];
                        const detail = first.reason?.response?.data?.detail || first.reason?.message || 'erreur';
                        toast.warning(`${realErrors.length} échec(s) lors de la création — ex : ${detail}`);
                      }
                      setShowPlanningModal(false);
                      setPlanningData({
                        team_leader_id: '',
                        collaborator_ids: [],
                        start_date: '',
                        end_date: '',
                        notes: '',
                        shift_type: 'day'
                      });
                    } catch (error) {
                      console.error('Erreur planification:', error);
                      toast.error('Erreur lors de la planification: ' + (error.response?.data?.detail || error.message));
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-black hover:to-gray-800 text-white rounded-xl py-3 font-semibold disabled:opacity-50"
                >
                  {saving ? (
                    <span className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      <span>Planification...</span>
                    </span>
                  ) : 'Planifier'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Détails du Créneau */}
      {showScheduleDetail && selectedSchedule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* En-tête */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    Détails de la Mission
                  </h2>
                  <p className="text-sm text-gray-500">
                    {new Date(selectedSchedule.start_date || selectedSchedule.date).toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
                <button
                  onClick={() => setShowScheduleDetail(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
                >
                  <X className="h-6 w-6 text-gray-500" />
                </button>
              </div>

              {/* Bannière chantier */}
              {(selectedSchedule.worksite_title || selectedSchedule.worksites?.title) && (
                <div className="bg-gradient-to-r from-blue-600 to-violet-600 rounded-xl p-4 mb-4 text-white">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl flex-shrink-0">🏗️</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-blue-200 mb-1">CHANTIER</div>
                      <div className="text-lg font-bold truncate">{selectedSchedule.worksite_title || selectedSchedule.worksites?.title}</div>
                      {(selectedSchedule.client_address || selectedSchedule.worksites?.address) && (
                        <div className="text-sm text-blue-100 mt-1 flex items-center gap-1">
                          <span>📍</span>
                          <span className="truncate">{selectedSchedule.client_address || selectedSchedule.worksites?.address}</span>
                        </div>
                      )}
                      {selectedSchedule.start_date && selectedSchedule.end_date && selectedSchedule.start_date !== selectedSchedule.end_date && (
                        <div className="text-sm text-blue-100 mt-1">
                          📅 {new Date(selectedSchedule.start_date).toLocaleDateString('fr-FR')} → {new Date(selectedSchedule.end_date).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Informations principales */}
              <div className="space-y-4">
                {selectedSchedule._ids?.length > 1 && (
                  <div>
                    <label htmlFor="mission-assignment" className="block text-sm font-semibold text-gray-700 mb-2">Affectation individuelle</label>
                    <select
                      id="mission-assignment"
                      value={selectedSchedule.id}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                      onChange={(event) => {
                        const member = schedules.find(schedule => schedule.id === event.target.value);
                        if (member) setSelectedSchedule(previous => ({
                          ...previous, ...member, _ids: previous._ids,
                          start_date: previous.start_date, end_date: previous.end_date,
                        }));
                      }}
                    >
                      {selectedSchedule._ids.map(id => {
                        const member = schedules.find(schedule => schedule.id === id);
                        return <option key={id} value={id}>{member ? getCollaboratorName(member.collaborator_id) : id}</option>;
                      })}
                    </select>
                  </div>
                )}
                {/* Client */}
                {selectedSchedule.client_name && (
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {selectedSchedule.client_name[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-indigo-600 font-semibold mb-0.5">CLIENT</div>
                        <div className="font-bold text-gray-900">{selectedSchedule.client_name}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Catégorie d'intervention */}
                {selectedSchedule.intervention_category && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
                    <span className="text-xl">🔧</span>
                    <div>
                      <div className="text-xs font-semibold text-amber-600">CATÉGORIE</div>
                      <div className="font-semibold text-gray-900 text-sm">{selectedSchedule.intervention_category}</div>
                    </div>
                  </div>
                )}

                {/* Horaires */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-xs text-gray-500 font-semibold mb-1">HORAIRES</div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedSchedule.time || '—'} - {selectedSchedule.end_time || '—'}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-xs text-gray-500 font-semibold mb-1">DURÉE</div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedSchedule.hours}h
                    </div>
                  </div>
                </div>

                {/* Équipe */}
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
                    <Users className="h-5 w-5 text-purple-600" />
                    <span>Équipe assignée</span>
                  </h3>
                  <div className="space-y-3">
                    {/* Chef d'équipe — résolu depuis teamLeaderMap via l'ID actif */}
                    {(() => {
                      const tl = selectedSchedule.team_leader_id && teamLeaderMap.get(selectedSchedule.team_leader_id);
                      return tl ? (
                        <div className="flex items-center space-x-3 bg-white rounded-lg p-3">
                          <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center font-bold text-purple-800">
                            👤
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {tl.prenom || tl.first_name} {tl.nom || tl.last_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Chef d'équipe
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Membre assigné — résolu depuis collaboratorMap via l'ID actif */}
                    {(() => {
                      const collab = selectedSchedule.collaborator_id && collaboratorMap.get(selectedSchedule.collaborator_id);
                      return collab ? (
                        <div className="flex items-center space-x-3 bg-white rounded-lg p-3">
                          <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center font-bold text-blue-800">
                            {(collab.first_name?.[0] || '') + (collab.last_name?.[0] || '')}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">
                              {collab.first_name} {collab.last_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {collab.phone || 'Téléphone non renseigné'}
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Description de la mission (schedule.description nettoyée) */}
                {selectedSchedule.description && selectedSchedule.description.replace(/^Planning pour\s*/i, '').trim() && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <span>📋</span>
                      <span>Description de la mission</span>
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {selectedSchedule.description.replace(/^Planning pour\s*/i, '').trim()}
                    </p>
                  </div>
                )}

                {/* Notes du chantier */}
                {selectedSchedule.worksites?.notes && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center space-x-2">
                      <span>📝</span>
                      <span>Notes</span>
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {selectedSchedule.worksites.notes}
                    </p>
                  </div>
                )}

                {/* 📸 Photos de la mission (uploadées par le technicien) */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <span>📸</span>
                    <span>Photos & documents de la mission</span>
                    {missionPhotos.length > 0 && (
                      <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                        {missionPhotos.length}
                      </span>
                    )}
                    <label className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${uploadingMissionPhoto ? 'bg-gray-200 text-gray-400 cursor-wait' : 'bg-purple-600 text-white hover:bg-purple-700'}`}>
                      {uploadingMissionPhoto ? '⏳ Envoi…' : '➕ Ajouter'}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        disabled={uploadingMissionPhoto}
                        onChange={handleBureauUploadMissionPhotos}
                      />
                    </label>
                  </h3>
                  <p className="text-[11px] text-gray-400 mb-3 -mt-1">Ajoutez un plan d'implantation, un schéma ou toute photo utile — le technicien y aura accès depuis sa mission.</p>
                  {missionPhotosLoading ? (
                    <p className="text-sm text-gray-400">Chargement des photos…</p>
                  ) : missionPhotos.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">Aucune photo pour le moment.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {missionPhotos.map((photo) => {
                        const d = photo.created_at ? new Date(photo.created_at) : null;
                        const dateStr = d ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                        const timeStr = d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
                        return (
                          <div key={photo.id} className="group rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                            <button
                              type="button"
                              onClick={() => setLightboxPhoto(photo)}
                              className="block w-full aspect-square overflow-hidden bg-gray-100"
                              title="Agrandir"
                            >
                              {photo.file_url ? (
                                <img
                                  src={photo.file_url}
                                  alt={photo.file_name || 'Photo mission'}
                                  loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300 text-3xl">🖼️</div>
                              )}
                            </button>
                            <div className="p-2 space-y-0.5">
                              <p className="text-xs font-semibold text-gray-800 truncate" title={photo.uploaded_by_name}>
                                👤 {photo.uploaded_by_name || 'Utilisateur'}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                📅 {dateStr}{timeStr && ` · ${timeStr}`}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <MissionComments scheduleId={selectedSchedule.id} />

                {/* Raison de pause */}
                {selectedSchedule.pause_reason && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                      <span>⏸</span>
                      <span>Motif de pause</span>
                      {selectedSchedule.pause_duration > 0 && (
                        <span className="ml-auto text-xs font-normal text-amber-600">{selectedSchedule.pause_duration} min en pause</span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedSchedule.pause_reason}</p>
                  </div>
                )}

                {/* Statut */}
                <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                  <span className="text-sm font-semibold text-gray-700">Statut</span>
                  {(() => {
                    const s = getEffectiveStatus(selectedSchedule);
                    const cfg = {
                      completed: { cls: 'bg-green-100 text-green-800', label: '✓ Terminé' },
                      partially_completed: { cls: 'bg-teal-100 text-teal-800', label: '◐ Partiellement terminé' },
                      cancelled: { cls: 'bg-red-100 text-red-800', label: '✗ Annulé' },
                      paused: { cls: 'bg-amber-100 text-amber-800', label: '⏸ En pause' },
                      unassigned: { cls: 'bg-orange-100 text-orange-800 border border-orange-300', label: '⚠️ Non assigné' },
                      in_progress: { cls: 'bg-orange-100 text-orange-800', label: '▶ En cours' },
                      scheduled: { cls: 'bg-blue-100 text-blue-800', label: '📅 Planifié' },
                    };
                    const c = cfg[s] || cfg.scheduled;
                    return <span className={`px-3 py-1 rounded-full text-xs font-bold ${c.cls}`}>{c.label}</span>;
                  })()}
                </div>

                {/* ═══ Bandeau Archive + Rapport + Historique (missions terminées) ═══ */}
                {['completed', 'partially_completed', 'cancelled'].includes(selectedSchedule.status) && (
                  <div className="space-y-3">
                    {/* Bandeau lecture seule */}
                    <div className="bg-gray-100 border border-gray-300 rounded-xl p-3 flex items-center gap-2">
                      <span className="text-lg">🔒</span>
                      <p className="text-sm text-gray-700 font-medium">Mission archivée — consultation uniquement</p>
                    </div>

                    {/* Rapport de clôture */}
                    {selectedSchedule.completion_report && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Rapport de clôture
                        </h3>
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedSchedule.completion_report}</p>
                        <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
                          {selectedSchedule.completed_at && (
                            <span>📅 {new Date(selectedSchedule.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          )}
                          {selectedSchedule.completed_by && (
                            <span>👤 {selectedSchedule.completed_by}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Historique / Timeline */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        Historique de la mission
                      </h3>
                      <div className="relative pl-6 space-y-3">
                        {/* Ligne de timeline verticale */}
                        <div className="absolute left-2 top-1 bottom-1 w-0.5 bg-gray-200"></div>

                        {/* Événement : Création */}
                        {selectedSchedule.created_at && (
                          <div className="relative">
                            <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-blue-400 border-2 border-white"></div>
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">Création</span>
                              <span className="text-gray-400 ml-2">{new Date(selectedSchedule.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            </p>
                          </div>
                        )}

                        {/* Événement : Planification */}
                        {selectedSchedule.start_date && (
                          <div className="relative">
                            <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-purple-400 border-2 border-white"></div>
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">Planifiée</span>
                              <span className="text-gray-400 ml-2">
                                du {new Date(selectedSchedule.start_date).toLocaleDateString('fr-FR')}
                                {selectedSchedule.end_date && selectedSchedule.end_date !== selectedSchedule.start_date && ` au ${new Date(selectedSchedule.end_date).toLocaleDateString('fr-FR')}`}
                              </span>
                            </p>
                          </div>
                        )}

                        {/* Événements : Cycles de pause/reprise depuis pause_history */}
                        {selectedSchedule.pause_history && Array.isArray(selectedSchedule.pause_history) && selectedSchedule.pause_history.map((cycle, idx) => (
                          <div key={`pause-${idx}`} className="relative space-y-2">
                            <div className="relative">
                              <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-amber-400 border-2 border-white"></div>
                              <p className="text-sm text-gray-700">
                                <span className="font-semibold">⏸ Pause</span>
                                {cycle.paused_at && <span className="text-gray-400 ml-2">{new Date(cycle.paused_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                                {cycle.pause_reason && <span className="text-gray-500 ml-1">— {cycle.pause_reason}</span>}
                              </p>
                            </div>
                            {cycle.resumed_at && (
                              <div className="relative">
                                <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-green-400 border-2 border-white"></div>
                                <p className="text-sm text-gray-700">
                                  <span className="font-semibold">▶ Reprise</span>
                                  <span className="text-gray-400 ml-2">{new Date(cycle.resumed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                  {cycle.pause_duration && <span className="text-gray-500 ml-1">({cycle.pause_duration} reportées)</span>}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Événement : Pause en cours (si paused_at existant mais pas dans history) */}
                        {selectedSchedule.status === 'paused' && selectedSchedule.paused_at && (
                          <div className="relative">
                            <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-amber-500 border-2 border-white animate-pulse"></div>
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">⏸ En pause</span>
                              <span className="text-gray-400 ml-2">{new Date(selectedSchedule.paused_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                              {selectedSchedule.pause_reason && <span className="text-gray-500 ml-1">— {selectedSchedule.pause_reason}</span>}
                            </p>
                          </div>
                        )}

                        {/* Événement : Clôture */}
                        {selectedSchedule.completed_at && (
                          <div className="relative">
                            <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-green-600 border-2 border-white"></div>
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">✅ Clôturée</span>
                              <span className="text-gray-400 ml-2">{new Date(selectedSchedule.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                              {selectedSchedule.completed_by && <span className="text-gray-500 ml-1">par {selectedSchedule.completed_by}</span>}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bandeau assignation rapide si unassigned */}
                {selectedSchedule.status === 'unassigned' && (
                  <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-orange-800 flex items-center gap-2">
                          <UserPlus className="h-4 w-4" />
                          Mission non assignée
                        </h3>
                        <p className="text-xs text-orange-600 mt-1">Aucun technicien assigné à cette mission</p>
                      </div>
                      <button
                        onClick={() => {
                          setShowScheduleDetail(false);
                          openAssignModal(selectedSchedule, 'single');
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-2"
                      >
                        <UserPlus className="w-4 h-4" />
                        Assigner
                      </button>
                    </div>
                  </div>
                )}

                {/* Raison de la pause */}
                {selectedSchedule.status === 'paused' && selectedSchedule.pause_reason && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h3 className="font-bold text-amber-800 mb-2 flex items-center space-x-2">
                      <Pause className="h-4 w-4" />
                      <span>Raison de la pause</span>
                    </h3>
                    <p className="text-sm text-amber-700 leading-relaxed">{selectedSchedule.pause_reason}</p>
                    {selectedSchedule.paused_at && (
                      <p className="text-xs text-amber-500 mt-2">
                        En pause depuis le {new Date(selectedSchedule.paused_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                )}

                {/* Historique pause / reprise */}
                {(() => {
                  const raw = selectedSchedule.pause_history;
                  const ph = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return []; } })() : raw;
                  if (!Array.isArray(ph) || ph.length === 0) return null;
                  return (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <h3 className="font-bold text-gray-700 mb-3 flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>Historique pause / reprise</span>
                    </h3>
                    <div className="space-y-3">
                      {ph.map((entry, idx) => (
                        <div key={idx} className="border-l-2 border-gray-300 pl-3 space-y-1">
                          <div className="flex items-center space-x-2">
                            <Pause className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            <span className="text-sm font-medium text-amber-700">Pause</span>
                            {entry.paused_at && (
                              <span className="text-xs text-gray-500">
                                — {new Date(entry.paused_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                          {entry.pause_reason && (
                            <p className="text-xs text-gray-600 ml-5.5">💬 {entry.pause_reason}</p>
                          )}
                          {entry.pause_duration && (
                            <p className="text-xs text-gray-600 ml-5.5">⏱ Durée à reporter : {entry.pause_duration}</p>
                          )}
                          {entry.resumed_at && (
                            <>
                              <div className="flex items-center space-x-2 mt-1">
                                <Play className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                <span className="text-sm font-medium text-green-700">Reprise</span>
                                <span className="text-xs text-gray-500">
                                  — {new Date(entry.resumed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              {entry.rescheduled_date && (
                                <p className="text-xs text-gray-500 ml-5.5">
                                  📅 Portion reportée au {new Date(entry.rescheduled_date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                              {/* Rétrocompat ancien format */}
                              {!entry.rescheduled_date && (entry.rescheduled_start || entry.rescheduled_end || entry.rescheduled_time) && (
                                <p className="text-xs text-gray-500 ml-5.5">
                                  📅 Reprogrammée :
                                  {entry.rescheduled_start && ` du ${new Date(entry.rescheduled_start + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                                  {entry.rescheduled_end && ` au ${new Date(entry.rescheduled_end + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                                  {entry.rescheduled_time && ` à ${entry.rescheduled_time}`}
                                </p>
                              )}
                              {entry.new_schedule_id && (
                                <p className="text-xs text-green-600 ml-5.5">✅ Nouveau planning créé</p>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })()}
              </div>

              {/* Actions */}
              <div className="flex flex-col space-y-3 mt-6 pt-6 border-t">
                {/* Bouton Voir le devis/chantier */}
                {selectedSchedule.worksites && (
                  <Button
                    onClick={() => {
                      // Si le chantier a un quote_id, ouvrir le devis, sinon afficher info toast
                      if (selectedSchedule.worksites.quote_id) {
                        window.open(`/devis/${selectedSchedule.worksites.quote_id}`, '_blank');
                      } else if (selectedSchedule.worksites.id) {
                        toast.info(`Chantier : ${selectedSchedule.worksites.title}`);
                      }
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 flex items-center justify-center space-x-2"
                  >
                    <span>📄</span>
                    <span>{selectedSchedule.worksites.quote_id ? 'Voir le Devis' : 'Infos Chantier'}</span>
                  </Button>
                )}
                
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => setShowScheduleDetail(false)}
                    variant="outline"
                    className="flex-1 rounded-xl py-3"
                  >
                    Fermer
                  </Button>

                  {/* Bouton Mettre en pause — visible si in_progress (auto-start compris) */}
                  {['scheduled', 'in_progress'].includes(getEffectiveStatus(selectedSchedule)) && (
                    <Button
                      onClick={() => {
                        setPauseReason('');
                        setPauseMode(getEffectiveStatus(selectedSchedule) === 'scheduled' ? 'postpone' : 'pause');
                        setPostponeOriginalDate(selectedSchedule.start_date || '');
                        setActionScope('single');
                        setShowPauseModal(true);
                      }}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-3 flex items-center justify-center space-x-2"
                    >
                      <Pause className="w-4 h-4" />
                      <span>{getEffectiveStatus(selectedSchedule) === 'scheduled' ? 'Reporter' : 'Pause / report'}</span>
                    </Button>
                  )}

                  {/* Bouton Clôturer — visible si scheduled, in_progress ou paused
                       (permet de clôturer une mission terminée en avance) */}
                  {['scheduled', 'in_progress', 'paused'].includes(getEffectiveStatus(selectedSchedule)) && (
                    <Button
                      onClick={() => {
                        setCompletionReport('');
                        setActionScope('single');
                        setShowCompleteModal(true);
                      }}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-3 flex items-center justify-center space-x-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Clôturer</span>
                    </Button>
                  )}

                  {/* Bouton Reprendre — visible si paused */}
                  {selectedSchedule.status === 'paused' && (
                    <Button
                      onClick={() => {
                        setResumeSchedule(selectedSchedule);
                        setResumeData({
                          start_date: '',
                          time: selectedSchedule.time || '',
                          team_leader_id: selectedSchedule.team_leader_id || '',
                          collaborator_id: selectedSchedule.collaborator_id || ''
                        });
                        setShowResumeModal(true);
                        setShowScheduleDetail(false);
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 flex items-center justify-center space-x-2"
                    >
                      <Play className="w-4 h-4" />
                      <span>Reprendre</span>
                    </Button>
                  )}

                  {/* Bouton Modifier — masqué pour les missions terminées/annulées */}
                  {!['completed', 'cancelled', 'partially_completed'].includes(selectedSchedule.status) && (
                    <Button
                      onClick={() => {
                        const startDate = selectedSchedule.start_date || selectedSchedule.period_start || selectedSchedule.date || '';
                        const endDate = selectedSchedule.end_date || selectedSchedule.period_end || startDate;
                        
                        setEditScheduleData({
                          start_date: startDate,
                          end_date: endDate,
                          team_leader_id: selectedSchedule.team_leader_id,
                          collaborator_id: selectedSchedule.collaborator_id
                        });
                        setShowEditScheduleModal(true);
                        setShowScheduleDetail(false);
                      }}
                      className="flex-1 bg-black hover:bg-gray-800 text-white rounded-xl py-3"
                    >
                      Modifier
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Modal Assignation Rapide ═══ */}
      {showAssignModal && assignTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center space-x-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {assignMode === 'batch' ? 'Assigner toutes les missions' : 'Assigner la mission'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {assignMode === 'batch'
                      ? `${assignTarget.count} mission${assignTarget.count > 1 ? 's' : ''} — ${assignTarget.worksite_title}`
                      : (assignTarget.worksite_title || assignTarget.worksites?.title || 'Mission')}
                  </p>
                </div>
              </div>

              {/* Sélection Chef d'équipe (optionnel) */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Chef d'équipe <span className="text-gray-400 text-xs">(optionnel)</span></label>
                <select
                  value={assignTeamLeaderId}
                  onChange={(e) => setAssignTeamLeaderId(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">— Aucun —</option>
                  {teamLeaders.map(tl => (
                    <option key={tl.id} value={tl.id}>{tl.prenom} {tl.nom}</option>
                  ))}
                </select>
              </div>

              {/* Sélection Technicien */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Technicien <span className="text-red-500">*</span></label>
                <select
                  value={assignCollaboratorId}
                  onChange={(e) => setAssignCollaboratorId(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">— Sélectionner —</option>
                  {collaborators.map(c => (
                    <option key={c.id} value={c.user_id || c.id}>
                      {c.first_name} {c.last_name} {c.phone ? `(${c.phone})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={() => { setShowAssignModal(false); setAssignTarget(null); }}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAssign}
                  disabled={!assignCollaboratorId || assignLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {assignLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Assignation...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      {assignMode === 'batch' ? `Assigner ${assignTarget.count} missions` : 'Assigner'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reprogrammer une journée reportée */}
      {showRescheduleModal && rescheduleOverride && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Reprogrammer la journée</h3>
                <button onClick={() => { setShowRescheduleModal(false); setRescheduleOverride(null); setRescheduleDate(''); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-sm text-gray-700">
                <p className="font-semibold">{rescheduleOverride.schedules?.worksite_title || 'Mission'}</p>
                <p className="text-gray-500 text-xs mt-0.5">Journée initiale : {new Date(rescheduleOverride.original_date).toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nouvelle date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={e => setRescheduleDate(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowRescheduleModal(false); setRescheduleOverride(null); setRescheduleDate(''); }}
                  className="flex-1 rounded-xl py-2.5"
                  disabled={rescheduleLoading}
                >
                  Annuler
                </Button>
                <Button
                  onClick={async () => {
                    if (!rescheduleDate) { toast.error('Veuillez choisir une date'); return; }
                    setRescheduleLoading(true);
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.patch(`${API}/planning-overrides/${rescheduleOverride.id}`, { new_date: rescheduleDate }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success(`Journée reprogrammée au ${new Date(rescheduleDate).toLocaleDateString('fr-FR')}`);
                      setOverrides(prev => prev.map(o => o.id === rescheduleOverride.id ? { ...o, new_date: rescheduleDate } : o));
                      setShowRescheduleModal(false);
                      setRescheduleOverride(null);
                      setRescheduleDate('');
                    } catch (err) {
                      toast.error('Erreur : ' + (err.response?.data?.detail || err.message));
                    } finally {
                      setRescheduleLoading(false);
                    }
                  }}
                  disabled={rescheduleLoading || !rescheduleDate}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2.5 font-semibold disabled:opacity-50"
                >
                  {rescheduleLoading ? 'En cours...' : 'Confirmer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox photo mission (agrandissement) */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxPhoto(null)}
              className="absolute -top-3 -right-3 w-9 h-9 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:bg-gray-100 z-10"
              title="Fermer"
            >
              ✕
            </button>
            {lightboxPhoto.file_url && (
              <img
                src={lightboxPhoto.file_url}
                alt={lightboxPhoto.file_name || 'Photo mission'}
                className="max-w-full max-h-[80vh] object-contain rounded-lg bg-black"
              />
            )}
            <div className="mt-3 bg-white/95 rounded-lg px-4 py-2 text-center">
              <p className="text-sm font-semibold text-gray-800">
                👤 {lightboxPhoto.uploaded_by_name || 'Utilisateur'}
              </p>
              {lightboxPhoto.created_at && (
                <p className="text-xs text-gray-500">
                  📅 {new Date(lightboxPhoto.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {' · '}
                  {new Date(lightboxPhoto.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Pause / Report enrichi */}
      {showPauseModal && selectedSchedule && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Pause className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Intervention</h2>
                  <p className="text-sm text-gray-500">
                    {selectedSchedule.worksites?.title || selectedSchedule.worksite_title || 'Mission'}
                  </p>
                </div>
              </div>

              {/* Radio: Pause simple / Reporter une journée */}
              <div className="mb-4 space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Type d'intervention</label>
                <div
                  onClick={() => { if (getEffectiveStatus(selectedSchedule) === 'in_progress') setPauseMode('pause'); }}
                  className={`flex items-center space-x-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    pauseMode === 'pause' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    pauseMode === 'pause' ? 'border-amber-500' : 'border-gray-300'
                  }`}>
                    {pauseMode === 'pause' && <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">⏸ Pause simple</p>
                    <p className="text-xs text-gray-500">{actionScope === 'group' ? `${actionMembers.length} affectations` : getCollaboratorName(selectedSchedule.collaborator_id)}</p>
                  </div>
                </div>
                <div
                  onClick={() => setPauseMode('postpone')}
                  className={`flex items-center space-x-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    pauseMode === 'postpone' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    pauseMode === 'postpone' ? 'border-blue-500' : 'border-gray-300'
                  }`}>
                    {pauseMode === 'postpone' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">🔁 Reporter une journée</p>
                    <p className="text-xs text-gray-500">Déplacer une journée à une autre date</p>
                  </div>
                </div>
              </div>

              {/* Champs pour le report */}
              {pauseMode === 'postpone' && (
                <div className="mb-4 space-y-3 bg-blue-50 rounded-xl p-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Journée à reporter <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={postponeOriginalDate}
                      onChange={(e) => setPostponeOriginalDate(e.target.value)}
                      min={selectedSchedule.start_date}
                      max={selectedSchedule.end_date}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nouvelle date *</label>
                    <input
                      type="date"
                      value={postponeNewDate}
                      onChange={(e) => setPostponeNewDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Durée à reporter (visible en mode pause simple) */}
              {pauseMode === 'pause' && renderActionScope()}
              {pauseMode === 'pause' && (
                <div className="mb-4 bg-amber-50 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Durée de travail à reporter <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={pauseDuration}
                      onChange={(e) => {
                        let v = e.target.value.replace(/[^0-9:]/g, '');
                        // Auto-format: ajouter ":" après 2 chiffres si pas déjà présent
                        if (v.length === 2 && !v.includes(':') && pauseDuration.length < v.length) v += ':';
                        if (v.length <= 5) setPauseDuration(v);
                      }}
                      placeholder="02:30"
                      maxLength={5}
                      className="w-32 rounded-xl border border-gray-300 px-4 py-2.5 text-sm text-center font-mono focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                    <span className="text-sm text-gray-500">heures : minutes</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Estimation de la durée de travail perdue à reprogrammer</p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Raison <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder={pauseMode === 'pause' ? 'Ex: Attente livraison matériaux, Intempéries...' : 'Ex: Météo défavorable, Indisponibilité client...'}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  rows={3}
                  autoFocus
                />
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={() => { setShowPauseModal(false); setPauseReason(''); setPauseMode('pause'); setPostponeOriginalDate(''); setPostponeNewDate(''); setPauseDuration(''); }}
                  variant="outline"
                  className="flex-1 rounded-xl py-3"
                  disabled={pauseLoading}
                >
                  Annuler
                </Button>
                <Button
                  onClick={async () => {
                    if (!pauseReason.trim()) {
                      toast.error('Veuillez saisir une raison');
                      return;
                    }
                    if (pauseMode === 'postpone' && (!postponeOriginalDate || !postponeNewDate)) {
                      toast.error('Veuillez renseigner la journée à reporter');
                      return;
                    }
                    setPauseLoading(true);
                    try {
                      const token = localStorage.getItem('token');
                      const headers = { Authorization: `Bearer ${token}` };
                      if (pauseMode === 'pause') {
                        // Pause simple avec durée à reporter
                        const response = await axios.patch(`${API}/schedules/${selectedSchedule.id}/pause`, {
                          pause_reason: pauseReason.trim(),
                          pause_duration: pauseDuration.trim(),
                          scope: actionScope,
                        }, { headers, timeout: 20000 });
                        const updatedRows = response.data.schedules || [response.data];
                        setSchedules(previous => previous.map(schedule => {
                          const updated = updatedRows.find(row => row.id === schedule.id);
                          return updated ? { ...schedule, ...updated } : schedule;
                        }));
                        toast.success(actionScope === 'group' ? `${updatedRows.length} affectations mises en pause` : 'Mission mise en pause');
                      } else {
                        // Report de journée
                        const res = await axios.post(`${API}/planning-overrides`, {
                          schedule_id: selectedSchedule.id,
                          original_date: postponeOriginalDate,
                          new_date: postponeNewDate || null,
                          reason: pauseReason.trim(),
                          override_type: 'postpone'
                        }, { headers, timeout: 8000 });
                        toast.success(postponeNewDate
                          ? `Journée du ${new Date(postponeOriginalDate).toLocaleDateString('fr-FR')} reportée au ${new Date(postponeNewDate).toLocaleDateString('fr-FR')}`
                          : `Journée du ${new Date(postponeOriginalDate).toLocaleDateString('fr-FR')} reportée (à programmer)`);
                        setOverrides(prev => prev.some(o => o.id === res.data.id) ? prev : [...prev, res.data]);
                      }
                      setShowPauseModal(false);
                      setShowScheduleDetail(false);
                      setSelectedSchedule(null);
                      setPauseReason('');
                      setPauseMode('pause');
                      setPostponeOriginalDate('');
                      setPostponeNewDate('');
                      setPauseDuration('');
                    } catch (err) {
                      toast.error('Erreur : ' + (err.response?.data?.detail || err.message));
                    } finally {
                      setPauseLoading(false);
                    }
                  }}
                  disabled={pauseLoading || !pauseReason.trim() || (pauseMode === 'pause' && !pauseDuration.match(/^\d{1,2}:\d{2}$/)) || (pauseMode === 'postpone' && (!postponeOriginalDate || !postponeNewDate))}
                  className={`flex-1 text-white rounded-xl py-3 ${pauseMode === 'pause' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {pauseLoading ? 'En cours...' : (pauseMode === 'pause' ? 'Confirmer la pause' : 'Confirmer le report')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal reprise — création d'une planification partielle */}
      {showResumeModal && resumeSchedule && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-900">Reprogrammer la portion interrompue</h2>
              <button
                onClick={() => { setShowResumeModal(false); setResumeSchedule(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {renderActionScope(resumeSchedule, resumeMembers, resumeData.scope || 'single', scope => setResumeData(previous => ({ ...previous, scope })), resumeLoading)}
              {/* Résumé mission d'origine */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="font-semibold text-gray-900">{resumeSchedule.worksite_title || resumeSchedule.worksites?.title || 'Mission'}</p>
                {resumeSchedule.pause_reason && (
                  <p className="text-sm text-amber-700 mt-1">Raison de pause : {resumeSchedule.pause_reason}</p>
                )}
                {resumeSchedule.paused_at && (
                  <p className="text-xs text-gray-500 mt-1">En pause depuis le {new Date(resumeSchedule.paused_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Mission initiale : {resumeSchedule.start_date && new Date(resumeSchedule.start_date + 'T00:00:00').toLocaleDateString('fr-FR')} - {resumeSchedule.end_date && new Date(resumeSchedule.end_date + 'T00:00:00').toLocaleDateString('fr-FR')}</p>
              </div>

              {/* Durée à reporter (lecture seule) */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">
                  {resumeSchedule.pause_duration || '—'}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Durée à reporter</p>
                  <p className="text-xs text-gray-500">Cette durée sera appliquée à la nouvelle planification</p>
                </div>
              </div>

              {/* Date de reprise (une seule date) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de reprise <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={resumeData.start_date}
                  onChange={e => setResumeData(prev => ({ ...prev, start_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Heure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure de début <span className="text-red-500">*</span></label>
                <input
                  type="time"
                  value={resumeData.time}
                  onChange={e => setResumeData(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Chef d'équipe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chef d'équipe</label>
                <select
                  value={resumeData.team_leader_id}
                  onChange={e => setResumeData(prev => ({ ...prev, team_leader_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">— Conserver l'équipe actuelle —</option>
                  {teamLeaders.map(tl => (
                    <option key={tl.id} value={tl.id}>{tl.prenom} {tl.nom}</option>
                  ))}
                </select>
              </div>

              {/* Collaborateur */}
              {resumeData.scope === 'group' ? resumeMembers.map((member, index) => (
                <label key={member.id} className="block text-sm font-medium text-gray-700">
                  {getCollaboratorName(member.collaborator_id)}
                  <select
                    aria-label={`Remplacer ${getCollaboratorName(member.collaborator_id)}`}
                    value={resumeData.collaborator_ids?.[index] || member.collaborator_id}
                    onChange={event => setResumeData(previous => ({ ...previous,
                      collaborator_ids: resumeMembers.map((row, memberIndex) => memberIndex === index ? event.target.value : previous.collaborator_ids?.[memberIndex] || row.collaborator_id)
                    }))}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {collaborators.map(collaborator => <option key={collaborator.id} value={collaborator.id}>{collaborator.first_name} {collaborator.last_name}</option>)}
                  </select>
                </label>
              )) : <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Technicien</label>
                <select
                  value={resumeData.collaborator_id}
                  onChange={e => setResumeData(prev => ({ ...prev, collaborator_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">— Conserver le technicien actuel —</option>
                  {collaborators.map(c => (
                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                  ))}
                </select>
              </div>}

              {/* Actions */}
              <div className="flex space-x-3 pt-2">
                <Button
                  onClick={() => { setShowResumeModal(false); setResumeSchedule(null); }}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl py-3"
                >
                  Annuler
                </Button>
                <Button
                  onClick={async () => {
                    setResumeLoading(true);
                    try {
                      const token = localStorage.getItem('token');
                      const payload = {
                        date: resumeData.start_date,
                        time: resumeData.time,
                        scope: resumeData.scope || 'single',
                        team_leader_id: resumeData.team_leader_id || null,
                        collaborator_id: resumeData.scope === 'group' ? null : resumeData.collaborator_id || null,
                        collaborator_ids: resumeData.scope === 'group' ? resumeMembers.map((member, index) => resumeData.collaborator_ids?.[index] || member.collaborator_id) : null
                      };

                      const res = await axios.patch(`${API}/schedules/${resumeSchedule.id}/resume`, payload, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Portion reportée planifiée avec succès');
                      // Mettre à jour le schedule d'origine (status partially_completed) dans le state
                      const originals = res.data.originals || (res.data.original ? [res.data.original] : []);
                      const newSchedules = res.data.new_schedules || (res.data.new_schedule ? [res.data.new_schedule] : []);
                      setSchedules(previous => {
                        const merged = new Map(previous.map(schedule => [schedule.id, schedule]));
                        [...originals, ...newSchedules].forEach(schedule => merged.set(schedule.id, { ...merged.get(schedule.id), ...schedule }));
                        return [...merged.values()];
                      });
                      setSelectedSchedule(previous => previous ? { ...previous, ...originals.find(row => row.id === previous.id) } : previous);
                      // Ajouter le nouveau schedule (portion reportée) dans le state
                      // (déduplication : le Realtime peut déjà l'avoir inséré)
                      setShowResumeModal(false);
                      setResumeSchedule(null);
                      // Naviguer vers la date de la reprise
                      if (resumeData.start_date) {
                        setCurrentDate(new Date(resumeData.start_date + 'T00:00:00'));
                      }
                    } catch (err) {
                      toast.error('Erreur : ' + (err.response?.data?.detail || err.message));
                    } finally {
                      setResumeLoading(false);
                    }
                  }}
                  disabled={resumeLoading || !resumeData.start_date || !resumeData.time}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resumeLoading ? 'En cours...' : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>Confirmer la reprise</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition du schedule */}
      {showEditScheduleModal && selectedSchedule && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-900">Modifier la Mission</h2>
              <button
                onClick={() => setShowEditScheduleModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {renderActionScope(selectedSchedule, actionMembers, editScheduleData.scope || 'single', scope => setEditScheduleData(previous => ({ ...previous, scope })), saving)}
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm font-semibold text-gray-700">Début
                  <Input type="time" aria-label="Heure de début" value={editScheduleData.time ?? selectedSchedule.time?.slice(0, 5) ?? ''} onChange={event => setEditScheduleData(previous => ({ ...previous, time: event.target.value }))} />
                </label>
                <label className="text-sm font-semibold text-gray-700">Fin
                  <Input type="time" aria-label="Heure de fin" value={editScheduleData.end_time ?? selectedSchedule.end_time?.slice(0, 5) ?? ''} onChange={event => setEditScheduleData(previous => ({ ...previous, end_time: event.target.value }))} />
                </label>
              </div>
              {/* Date de début */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📅 Date de début
                </label>
                <Input
                  type="date"
                  value={editScheduleData.start_date}
                  onChange={(e) => setEditScheduleData({ ...editScheduleData, start_date: e.target.value })}
                  className="w-full"
                />
              </div>

              {/* Date de fin */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📅 Date de fin
                </label>
                <Input
                  type="date"
                  value={editScheduleData.end_date}
                  onChange={(e) => setEditScheduleData({ ...editScheduleData, end_date: e.target.value })}
                  className="w-full"
                />
              </div>

              {/* Chef d'équipe */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  👨‍💼 Chef d'équipe
                </label>
                <select
                  value={editScheduleData.team_leader_id}
                  onChange={(e) => setEditScheduleData({ ...editScheduleData, team_leader_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un chef d'équipe</option>
                  {teamLeaders.map(leader => (
                    <option key={leader.id} value={leader.id}>
                      {leader.prenom} {leader.nom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Collaborateur */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  👤 Collaborateur
                </label>
                <select
                  value={editScheduleData.collaborator_id}
                  disabled={editScheduleData.scope === 'group'}
                  onChange={(e) => setEditScheduleData({ ...editScheduleData, collaborator_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un collaborateur</option>
                  {collaborators.map(collab => (
                    <option key={collab.id} value={collab.id}>
                      {collab.first_name} {collab.last_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Boutons d'action */}
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={() => setShowEditScheduleModal(false)}
                  variant="outline"
                  className="flex-1 rounded-xl py-3"
                >
                  Annuler
                </Button>
                <Button
                  disabled={saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const token = localStorage.getItem('token');
                      const headers = { 'Authorization': `Bearer ${token}` };
                      
                      // Modifier le schedule (dates + chef d'équipe + collaborateur)
                      const response = await axios.put(`${API}/schedules/${selectedSchedule.id}`, {
                        scope: editScheduleData.scope || 'single',
                        team_leader_id: editScheduleData.team_leader_id,
                        ...(editScheduleData.scope === 'group' ? {} : { collaborator_id: editScheduleData.collaborator_id }),
                        period_start: editScheduleData.start_date,
                        period_end: editScheduleData.end_date,
                        time: editScheduleData.time ?? selectedSchedule.time?.slice(0, 5),
                        end_time: editScheduleData.end_time ?? selectedSchedule.end_time?.slice(0, 5)
                      }, { headers });

                      // Mise à jour optimiste du schedule (dates + équipe)
                      const updatedRows = response.data.schedules || [response.data];
                      setSchedules(previous => previous.map(schedule => ({ ...schedule, ...updatedRows.find(row => row.id === schedule.id) })));
                      setSelectedSchedule(previous => previous ? { ...previous, ...updatedRows.find(row => row.id === previous.id) } : previous);
                      setShowEditScheduleModal(false);
                      toast.success('Mission modifiée avec succès !');
                    } catch (error) {
                      console.error('Erreur modification schedule:', error);
                      toast.error('Erreur lors de la modification: ' + (error.response?.data?.detail || error.message));
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3 disabled:opacity-50"
                >
                  {saving ? (
                    <span className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      <span>Enregistrement...</span>
                    </span>
                  ) : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Modal Clôture de Mission ═══ */}
      {showCompleteModal && selectedSchedule && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Clôturer la mission
                </h2>
                <button onClick={() => setShowCompleteModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Mission : <strong>{selectedSchedule.worksite_title || selectedSchedule.client_name || 'Sans titre'}</strong>
              </p>

              {renderActionScope()}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📝 Rapport de clôture <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={completionReport}
                  onChange={(e) => setCompletionReport(e.target.value)}
                  placeholder="Décrivez les travaux réalisés, observations, réserves éventuelles..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent min-h-[120px] resize-y"
                  maxLength={2000}
                />
                <p className="text-xs text-gray-400 mt-1">{completionReport.length}/2000 caractères</p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-emerald-800">
                  ✅ La mission sera marquée comme <strong>terminée</strong>. Cette action est irréversible.
                </p>
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowCompleteModal(false)}
                  variant="outline"
                  className="flex-1 rounded-xl py-3"
                  disabled={completeLoading}
                >
                  Annuler
                </Button>
                <Button
                  disabled={completeLoading || !completionReport.trim()}
                  onClick={async () => {
                    setCompleteLoading(true);
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.patch(
                        `${API}/schedules/${selectedSchedule.id}/complete`,
                        { completion_report: completionReport.trim(), scope: actionScope },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      // Mise à jour optimiste
                      const updatedRows = res.data.schedules || [res.data];
                      setSchedules(previous => previous.map(schedule => {
                        const updated = updatedRows.find(row => row.id === schedule.id);
                        return updated ? { ...schedule, ...updated } : schedule;
                      }));
                      setSelectedSchedule(previous => previous ? { ...previous, ...updatedRows.find(row => row.id === previous.id) } : previous);
                      setShowCompleteModal(false);
                      setShowScheduleDetail(false);
                      toast.success(actionScope === 'group' ? `${updatedRows.length} affectations clôturées` : 'Mission clôturée avec succès !');
                    } catch (error) {
                      console.error('Erreur clôture:', error);
                      toast.error('Erreur : ' + (error.response?.data?.detail || error.message));
                    } finally {
                      setCompleteLoading(false);
                    }
                  }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-3 disabled:opacity-50"
                >
                  {completeLoading ? 'Clôture en cours...' : 'Confirmer la clôture'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningManagement;